'use client';
import React, { useEffect, useState } from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { personnelReel } from '@/lib/comptes';
import type { Engagements, EngagementLigne, TransfertPayload } from '@/lib/store';
import { GED_ROLES, ORG_TIERS, tierOfUser, estAffectable, libelleIndisponible, ORG_REFS_DEFAUT, aujourdhuiIso, fmtDate } from '@/lib/meta';
import { I, card, inputStyle, useEscape, ConfirmButton, Selecteur, useConfirmation, ChampRecherche, Modale, SelecteurPastilles } from '@/components/ui';
import type { GedUser, OrgFonction, RapprochementComptes } from '@/lib/types';
import { initials, H1 } from './commun';
import { ResumePersonne } from './ResumePersonne';
import { FormulaireArrivee } from './Arrivee';
import { PanneauFicheFonction } from './FicheFonction';
import { pastilleDe } from '@/lib/couleurs-personne';
import { pastilleCompte, titrePastille } from '@/lib/etat-compte-vue';
import { ArbreOrganigramme, type PoigneesArbre } from '@/components/ArbreOrganigramme';
import { nomDe } from '@/lib/nom';
import { organigrammeAffiche } from '@/lib/org-affiche';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
// Organigramme, engagements d'une personne et transfert de responsabilités.

/* ──────────────── Les fiches de FONCTION (RQ, 26/08/2026) ──────────────────
 *
 * ⚠️ LA FONCTION EST LE MÉTIER, et elle n'est ni le poste ni le processus.
 * L'organigramme fonctionnel portait dix-sept cases pour dix-huit personnes
 * parce que les trois notions y étaient fondues : six pilotages de processus,
 * deux suppléances, trois postes de planning, trois vrais métiers et trois
 * référents de discipline. Il n'y a que SIX fonctions.
 *
 * ⚠️ LES FICHES SONT MODIFIABLES, et c'est le point : elles vivent en base et
 * non dans le code. Quatre d'entre elles ont été AMORCÉES — d'après une fiche
 * périmée pour le technicien, d'après l'organisation observée pour les trois
 * autres — et portent une réserve tant que personne ne les a relues.
 */

interface FicheFonction {
  documentId: string; code: string; libelle: string; fiche: string;
  amplitude: string; missions: string[]; profil: string[];
  liaisonsHierarchiques: string; liaisonsFonctionnelles: string;
  moyens: string; contraintes: string; conditions: string;
  qualifications: string; engagement: string; reserve: string;
  modifiePar: string; modifieLe: string;
  /* Circuit léger (01/09/2026). `reference` est celle de la codification de la
     Qualité ; `fiche` reste l'ancienne référence papier des classeurs. */
  reference: string; version: string; etat: string; etatLabel: string;
  dateApplication: string;
  verifiePar: string; verifieLe: string; approuvePar: string; approuveLe: string;
}

/** Couleurs des trois états. « Approuvée » n'est pas « en vigueur » : la date
 *  d'application peut être à venir. */
const ETATS_FICHE: Record<string, { bg: string; fg: string }> = {
  brouillon: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  averifier: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  approuvee: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
};

const CHAMPS_FICHE: { cle: keyof FicheFonction; titre: string; liste?: boolean; aide?: string }[] = [
  { cle: 'libelle', titre: 'Désignation' },
  /* ⚠️ CE CHAMP N'EST PLUS *LA* RÉFÉRENCE depuis le 01/09/2026 : c'est celle
     des classeurs. La référence du système est attribuée par la codification
     et ne se saisit pas — elle s'attribue d'un bouton, une fois. */
  { cle: 'fiche', titre: 'Ancienne référence papier', aide: 'Celle du classeur (« ORG.G1.08 »). Sert à retrouver l’original, pas à identifier la fiche ici.' },
  { cle: 'amplitude', titre: 'Amplitude horaire' },
  { cle: 'missions', titre: 'Missions clés', liste: true, aide: 'Une mission par ligne.' },
  { cle: 'profil', titre: 'Profil attendu', liste: true, aide: 'Une exigence par ligne — c’est ici que se dit le diplôme.' },
  { cle: 'liaisonsHierarchiques', titre: 'Liaisons hiérarchiques' },
  { cle: 'liaisonsFonctionnelles', titre: 'Liaisons fonctionnelles' },
  { cle: 'moyens', titre: 'Moyens techniques', aide: 'Les moyens dépendent du POSTE occupé, pas de la fonction.' },
  { cle: 'contraintes', titre: 'Contraintes' },
  { cle: 'conditions', titre: 'Conditions de travail' },
  { cle: 'qualifications', titre: 'Qualifications' },
];

function FichesDeFonction({ estRQ, estRH }: { estRQ: boolean; estRH: boolean }) {
  const showToast = useGed((s) => s.showToast);
  const moi = useGed((s) => s.session.name);
  const [fiches, setFiches] = useState<FicheFonction[] | null>(null);
  const [phrase, setPhrase] = useState('');
  const [ouverte, setOuverte] = useState<string | null>(null);
  /* Les modifications saisies dans la fiche ouverte et pas encore enregistrées :
     le circuit (vérifier, approuver) porte sur la version ENREGISTRÉE. */
  const [modifs, setModifs] = useState(false);

  const charger = React.useCallback(() => {
    void fetch('/api/ged/ged/fonctions', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.ok) { setFiches(d.fonctions as FicheFonction[]); setPhrase(String(d.phrase || '')); } else setFiches([]); })
      .catch(() => setFiches([]));
  }, []);
  React.useEffect(() => { charger(); }, [charger]);

  if (!fiches) return <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement…</p>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '74ch' }}>
        La fonction est le <strong>métier</strong> d’une personne — ce qu’elle est. Elle se distingue du
        <strong> poste</strong>, qui dit où l’on travaille un jour donné, et du <strong>pilotage d’un
        processus</strong>, qui dit ce que l’on porte en permanence. Une fiche de fonction peut décrire des
        responsabilités qui ne sont pas nécessaires pour tenir un poste.
      </p>
      {phrase && (
        <div style={{
          padding: '9px 12px', borderRadius: 'var(--radius-md)',
          background: 'var(--amber-50)', border: '1px solid var(--amber-500)', fontSize: 12.5,
        }}>
          {phrase}
        </div>
      )}

      {fiches.map((f) => (
        <div key={f.code} style={{ ...card, padding: '12px 16px' }}>
          <button type="button" onClick={() => setOuverte(ouverte === f.code ? null : f.code)}
            aria-expanded={ouverte === f.code} className="ged-btn"
            style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'start', font: 'inherit' }}>
            <strong style={{ fontSize: 14 }}>{f.libelle}</strong>
            {/* La RÉFÉRENCE DU SYSTÈME d'abord, le code interne ensuite : c'est
                la première qu'on cite en audit. Son absence se dit, elle ne se
                laisse pas deviner d'un blanc. */}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: f.reference ? 'var(--text-strong)' : 'var(--amber-600)' }}>
              {f.reference || 'sans référence'}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{f.code}</span>
            <span style={{ flex: 1 }} />
            <span style={{
              fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px',
              background: (ETATS_FICHE[f.etat] || ETATS_FICHE.brouillon).bg,
              color: (ETATS_FICHE[f.etat] || ETATS_FICHE.brouillon).fg,
            }}>v{f.version} · {f.etatLabel}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {f.missions.length} mission(s)
            </span>
            {/* La réserve se voit dans la LISTE, pas seulement une fois la fiche
                ouverte : sinon la page se lit « c’est fait ». */}
            {f.reserve && (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>à relire</span>
            )}
            <span aria-hidden style={{ color: 'var(--text-faint)' }}>{ouverte === f.code ? '−' : '+'}</span>
          </button>

          {ouverte === f.code && (
            <>
              <CircuitFiche fiche={f} estRQ={estRQ} estRH={estRH} moi={moi} nonEnregistre={modifs}
                onFait={(msg) => { charger(); showToast(msg); }}
                onErreur={(msg) => showToast(msg, 'erreur')} />
              <FicheEditable fiche={f} estRQ={estRQ} onModifie={setModifs}
                onEnregistre={(msg) => { charger(); showToast(msg); }} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

const MSG_NON_ENREGISTRE = 'Modifications non enregistrées : enregistrez-les avant de vérifier ou d’approuver — le circuit porte sur la version enregistrée.';

/**
 * LE CIRCUIT D'UNE FICHE — référence, vérification, approbation (01/09/2026).
 *
 * ⚠️ IL EXISTE PARCE QUE LES ROUTES EXISTENT. Poser un circuit au serveur sans
 * bouton pour l'appeler, c'est le défaut le plus cher de ce projet : la route
 * est écrite, testée, journalisée — et jamais atteinte. Chaque acte du
 * contrôleur a ici son bouton, et un seul.
 *
 * ⚠️ LES REFUS VIENNENT DU SERVEUR ET SONT MONTRÉS TELS QUELS. La séparation
 * des tâches (on ne vérifie pas ce qu'on a rédigé) se juge sur ce qui est écrit
 * dans la fiche : la recopier ici donnerait un écran qui autorise ce que le
 * serveur refuse, ou l'inverse. On appelle, et on affiche la phrase reçue.
 */
function CircuitFiche({ fiche, estRQ, estRH, moi, nonEnregistre = false, onFait, onErreur }: {
  fiche: FicheFonction; estRQ: boolean; estRH: boolean;
  /** La fiche affichée porte des modifications pas encore enregistrées. Vérifier
   *  ou approuver agirait sur la version STOCKÉE, pas sur ce qu'on lit à
   *  l'écran : on grise le geste et on dit pourquoi (09/10/2026). */
  nonEnregistre?: boolean;
  /** Comparé à `fiche.modifiePar`/`verifiePar` — jamais proposer un acte que
   *  le serveur refusera de toute façon (ISO 15189 §8.3, séparation des
   *  tâches ; même correctif que l'organigramme et la cartographie). */
  moi: string;
  onFait: (msg: string) => void; onErreur: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [motif, setMotif] = useState<string | null>(null);
  const [dateApp, setDateApp] = useState(() => aujourdhuiIso());

  const appeler = async (chemin: string, corps: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/fonctions/${encodeURIComponent(fiche.code)}/${chemin}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        /* La version LUE voyage avec le visa (14/10/2026) : si la fiche a été
           modifiée depuis son affichage, le moteur refuse (409) au lieu de
           viser un texte que personne ici n'a relu. */
        body: JSON.stringify({ ...corps, modifieLe: fiche.modifieLe || '' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { onErreur(j?.error?.message || 'Acte refusé.'); return false; }
      onFait(String(j?.message || 'Fait.'));
      return true;
    } catch {
      onErreur('Acte impossible — le serveur n’a pas répondu.');
      return false;
    } finally { setBusy(false); }
  };

  const etat = fiche.etat || 'brouillon';
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius-md)',
      background: 'var(--gray-25)', border: '1px solid var(--border-default)',
    }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Version {fiche.version} · {fiche.etatLabel}
          {etat === 'approuvee' && fiche.dateApplication && ` · applicable le ${fiche.dateApplication}`}
        </span>
        <span style={{ flex: 1 }} />

        {/* La référence s'attribue UNE FOIS, et le bouton disparaît ensuite :
            un bouton qui ne peut plus qu'échouer n'a rien à faire à l'écran. */}
        {estRQ && !fiche.reference && (
          <Bouton taille="sm" disabled={busy} onClick={() => void appeler('reference', {})}>
            Attribuer une référence
          </Bouton>
        )}
        {/* ⚠️ ISO 15189 §8.3, MONTRÉ AVANT D'ÊTRE REFUSÉ (05/09/2026 — même
            correctif que l'organigramme et la cartographie). Proposer
            « Vérifier » à qui vient de rédiger pour laisser le serveur seul
            le refuser ne dit rien de ce qui se passe réellement : la fiche
            attend quelqu'un d'autre, pas une action bloquée sans raison. */}
        {etat === 'brouillon' && (
          fiche.modifiePar && fiche.modifiePar === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Rédigée par vous — en attente de vérification par quelqu’un d’autre.
            </span>
          ) : (estRQ || estRH) && (
            <Bouton taille="sm" disabled={busy || nonEnregistre} title={nonEnregistre ? MSG_NON_ENREGISTRE : fiche.reserve ? 'La réserve doit être levée avant la vérification.' : undefined} onClick={() => void appeler('circuit', { acte: 'verifier' })}>
              <I n="check" s={12} />Vérifier
            </Bouton>
          )
        )}
        {etat === 'averifier' && estRQ && (
          fiche.verifiePar && fiche.verifiePar === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Vérifiée par vous — en attente d’approbation par quelqu’un d’autre.
            </span>
          ) : (
            <>
              <input type="date" value={dateApp} aria-label="Date d’application"
                onChange={(e) => setDateApp(e.target.value)}
                style={{ ...inputStyle, width: 150 }} />
              <Bouton variante="primaire" taille="sm" disabled={busy || nonEnregistre} title={nonEnregistre ? MSG_NON_ENREGISTRE : undefined} onClick={() => void appeler('circuit', { acte: 'approuver', dateApplication: dateApp })}>
                <I n="check" s={12} />Approuver
              </Bouton>
            </>
          )
        )}
        {/* LE RENVOI répond à « et si ça ne lui plaît pas ? » : le
            vérificateur (ou le RQ/RH) rend la fiche en rédaction, motif
            obligatoire — déjà câblé, pas une fonction à ajouter. */}
        {etat !== 'brouillon' && (estRQ || estRH) && (
          <Bouton taille="sm" disabled={busy} onClick={() => setMotif('')}>
            Renvoyer en rédaction
          </Bouton>
        )}
      </div>

      {nonEnregistre && etat !== 'approuvee' && (
        <p role="status" style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
          {MSG_NON_ENREGISTRE}
        </p>
      )}

      {/* Le motif est OBLIGATOIRE côté serveur : le champ s'ouvre ici plutôt que
          de laisser partir un appel qui sera refusé. */}
      {motif !== null && (
        <div style={{ marginTop: 9, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={motif} autoFocus placeholder="Ce qui doit être repris — obligatoire"
            aria-label="Motif du renvoi" style={{ ...inputStyle, flex: 1 }}
            onChange={(e) => setMotif(e.target.value)} />
          <Bouton taille="sm" onClick={() => setMotif(null)}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={busy || !motif.trim()} onClick={async () => { if (await appeler('circuit', { acte: 'renvoyer', motif })) setMotif(null); }}>
            Renvoyer
          </Bouton>
        </div>
      )}

      <p style={{ margin: '9px 0 0', fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.5 }}>
        Modifier une fiche <strong>approuvée</strong> ouvre une révision : la version passe à la
        suivante et les visas sont à refaire — une fiche en vigueur ne se corrige pas en place.
        La signature du titulaire reste papier, dans le dossier d’intégration.
      </p>
    </div>
  );
}

/** Même vocabulaire que le circuit léger des fiches de fonction (`ETAT_LABEL`,
 *  `backend/src/api/ged/controllers/fonction.ts`) — l'organigramme suit
 *  désormais le même circuit RED/VÉR/APP (Lot 4, 03/09/2026). */
const ETAT_ORG_LABEL: Record<string, string> = {
  brouillon: 'brouillon', averifier: 'à vérifier', approuvee: 'approuvée',
};

interface CircuitOrg {
  version: string; etat: 'brouillon' | 'averifier' | 'approuvee';
  redacteur?: string; redigeLe?: string;
  verifiePar?: string; verifieLe?: string; approuvePar?: string; approuveLe?: string;
  dateApplication?: string;
}
const CIRCUIT_ORG_DEFAUT: CircuitOrg = { version: '01', etat: 'approuvee' };

/**
 * LE BANDEAU DE CIRCUIT DE L'ORGANIGRAMME — même gabarit que `CircuitFiche`.
 *
 * ⚠️ Exigence non négociable de la spec (Lot 4 §Gouvernance) : « le changement
 * doit incrémenter la version du document concerné et suivre le circuit de
 * validation existant (RED/VÉR/APP), pas contourner ce circuit ». Chaque
 * re-rattachement OUVRE ou POURSUIT une révision (arbitrage Q2/Q6, « brouillon
 * puis validation groupée ») ; ce bandeau est ce qui la fait ensuite avancer.
 *
 * Permanent, comme demandé — mais SANS compteur de gestes : le serveur ne tient
 * pas ce compte (il ne tient que l'état final), et l'inventer côté écran
 * afficherait un nombre qu'un rechargement de page ne pourrait pas retrouver.
 * L'état et la version, eux, sont la vérité serveur.
 */
function CircuitOrganigramme({ vue, circuit, isRQ, estRH, moi, onFait, onErreur }: {
  vue: 'nominatif' | 'fonctionnel'; circuit: CircuitOrg; isRQ: boolean; estRH: boolean;
  /** Le nom affiché de la session courante — comparé à `circuit.redacteur` et
   *  `circuit.verifiePar` pour ne JAMAIS proposer un acte que le serveur
   *  refusera de toute façon (ISO 15189 §8.3, séparation des tâches). */
  moi: string;
  onFait: (msg: string) => void; onErreur: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [motif, setMotif] = useState<string | null>(null);
  const [dateApp, setDateApp] = useState(() => aujourdhuiIso());
  const etat = circuit.etat || 'approuvee';
  if (etat === 'approuvee' && !isRQ && !estRH) return null;

  const appeler = async (corps: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/rh/organigramme/${vue}/circuit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { onErreur(j?.error?.message || 'Acte refusé.'); return false; }
      onFait(String(j?.message || 'Fait.'));
      return true;
    } catch {
      onErreur('Acte impossible — le serveur n’a pas répondu.');
      return false;
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      padding: '9px 12px', borderRadius: 'var(--radius-md)',
      background: etat === 'brouillon' ? 'var(--amber-50)' : 'var(--gray-25)',
      border: `1px solid ${etat === 'brouillon' ? 'var(--amber-500)' : 'var(--border-default)'}`,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: etat === 'brouillon' ? 700 : 400 }}>
          {vue === 'nominatif' ? 'BIO24-A-ORG-02' : 'BIO24-A-ORG-01'} — version {circuit.version} · {ETAT_ORG_LABEL[etat]}
          {etat === 'brouillon' && ' — modifications non validées'}
          {etat === 'approuvee' && circuit.dateApplication && ` · applicable le ${circuit.dateApplication}`}
        </span>
        <span style={{ flex: 1 }} />
        {/* ⚠️ ISO 15189 §8.3, MONTRÉ AVANT D'ÊTRE REFUSÉ. Le bouton « Vérifier »
            n'apparaissait pas moins pour son propre rédacteur — qui le
            cliquait pour se voir répondre par le serveur, seul, ce que
            l'écran aurait dû dire depuis le début : quelqu'un d'autre doit
            vérifier. */}
        {etat === 'brouillon' && (
          circuit.redacteur && circuit.redacteur === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Rédigé par vous — en attente de vérification par quelqu’un d’autre.
            </span>
          ) : (isRQ || estRH) && (
            <Bouton taille="sm" disabled={busy} onClick={() => void appeler({ acte: 'verifier' })}>
              <I n="check" s={12} />Vérifier
            </Bouton>
          )
        )}
        {etat === 'averifier' && isRQ && (
          circuit.verifiePar && circuit.verifiePar === moi ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Vérifié par vous — en attente d’approbation par quelqu’un d’autre.
            </span>
          ) : (
            <>
              <input type="date" value={dateApp} aria-label="Date d’application"
                onChange={(e) => setDateApp(e.target.value)}
                style={{ ...inputStyle, width: 150 }} />
              <Bouton variante="primaire" taille="sm" disabled={busy} onClick={() => void appeler({ acte: 'approuver', dateApplication: dateApp })}>
                <I n="check" s={12} />Approuver
              </Bouton>
            </>
          )
        )}
        {/* LE RENVOI EST LA RÉPONSE À « et si ça ne lui plaît pas ? » — le
            vérificateur (ou le RQ) peut rendre la révision en rédaction, avec
            un motif obligatoire : c'est ce bouton, déjà câblé, pas une
            fonction à ajouter. */}
        {etat !== 'brouillon' && etat !== 'approuvee' && (isRQ || estRH) && (
          <Bouton taille="sm" disabled={busy} onClick={() => setMotif('')}>
            Renvoyer en rédaction
          </Bouton>
        )}
      </div>
      {motif !== null && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={motif} autoFocus placeholder="Ce qui doit être repris — obligatoire"
            aria-label="Motif du renvoi" style={{ ...inputStyle, flex: 1 }}
            onChange={(e) => setMotif(e.target.value)} />
          <Bouton taille="sm" onClick={() => setMotif(null)}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={busy || !motif.trim()} onClick={async () => { if (await appeler({ acte: 'renvoyer', motif })) setMotif(null); }}>
            Renvoyer
          </Bouton>
        </div>
      )}
    </div>
  );
}

function valeursFiche(fiche: FicheFonction): Record<string, string> {
  const o: Record<string, string> = {};
  for (const c of CHAMPS_FICHE) {
    const v = fiche[c.cle];
    o[c.cle as string] = Array.isArray(v) ? v.join('\n') : String(v ?? '');
  }
  return o;
}

function FicheEditable({ fiche, estRQ, onEnregistre, onModifie }: {
  fiche: FicheFonction; estRQ: boolean; onEnregistre: (msg: string) => void;
  /** Dit au parent si la saisie diffère de la fiche enregistrée. */
  onModifie?: (modifie: boolean) => void;
}) {
  const [f, setF] = useState<Record<string, string>>(() => valeursFiche(fiche));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), joint sous `base` :
     le moteur n'écrit que les rubriques changées depuis cette lecture. */
  const [base] = useState<Record<string, string>>(() => valeursFiche(fiche));
  const enregistree = valeursFiche(fiche);
  /* ⚠️ Comparer À LA FAÇON DU SERVEUR (`txt` / liste dans `fonction.ts`) : il
     rogne les espaces et supprime les lignes vides à l'enregistrement. Sans
     cela, un espace final laisserait la saisie « différente » de la fiche
     enregistrée pour toujours, et le circuit resterait grisé sans issue. */
  const norme = (c: (typeof CHAMPS_FICHE)[number], v: string | undefined) => (c.liste
    ? String(v ?? '').split('\n').map((x) => x.trim()).filter(Boolean).join('\n')
    : String(v ?? '').trim());
  const modifie = CHAMPS_FICHE.some((c) => norme(c, f[c.cle as string]) !== norme(c, enregistree[c.cle as string]));
  useEffect(() => { onModifie?.(modifie); }, [modifie, onModifie]);
  useEffect(() => () => onModifie?.(false), [onModifie]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const poster = async (corps: Record<string, unknown>, succes: string) => {
    setEnvoi(true); setErreur('');
    try {
      const r = await fetchEcriture(`/api/ged/ged/fonctions/${fiche.code}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.error?.message || 'Enregistrement refusé');
      onEnregistre(String(d?.message || succes));
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement refusé');
    } finally { setEnvoi(false); }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 10 }}>
      {fiche.reserve && (
        <div style={{ padding: '9px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50)', border: '1px solid var(--amber-500)', fontSize: 12.5, lineHeight: 1.5 }}>
          {fiche.reserve}
        </div>
      )}

      {CHAMPS_FICHE.map((c) => (
        <div key={String(c.cle)}>
          <label htmlFor={`f-${fiche.code}-${String(c.cle)}`} style={etiquetteChamp}>{c.titre}</label>
          {c.liste ? (
            <textarea id={`f-${fiche.code}-${String(c.cle)}`} rows={Math.max(3, (f[c.cle as string] || '').split('\n').length)}
              value={f[c.cle as string] || ''} disabled={!estRQ}
              onChange={(e) => setF((p) => ({ ...p, [c.cle as string]: e.target.value }))}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
          ) : (
            <input id={`f-${fiche.code}-${String(c.cle)}`} value={f[c.cle as string] || ''} disabled={!estRQ}
              onChange={(e) => setF((p) => ({ ...p, [c.cle as string]: e.target.value }))}
              style={inputStyle} />
          )}
          {c.aide && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{c.aide}</div>}
        </div>
      ))}

      {fiche.modifiePar && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Dernière modification : {fiche.modifiePar}{fiche.modifieLe ? ` le ${fmtDate(fiche.modifieLe)}` : ''}
        </div>
      )}
      {erreur && <p role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5, margin: 0 }}>{erreur}</p>}

      {estRQ && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* ⚠️ DEUX BOUTONS ET NON UN. Enregistrer un texte ne veut pas dire
              « j’ai relu la fiche » : sans cette séparation, la réserve
              tomberait au premier ajustement de virgule. */}
          {fiche.reserve && (
            <Bouton taille="sm" type="button" disabled={envoi || modifie} title={modifie ? 'Enregistrez d’abord vos modifications : la relecture porte sur la version enregistrée.' : 'Efface la réserve : vous déclarez avoir relu cette fiche.'} onClick={() => void poster({ releve: true }, 'Réserve levée.')}>
              J’ai relu cette fiche
            </Bouton>
          )}
          <Bouton variante="primaire" taille="sm" type="button" disabled={envoi} onClick={() => void poster(
              { ...Object.fromEntries(CHAMPS_FICHE.map((c) => [c.cle, f[c.cle as string] || ''])), base },
              'Fiche enregistrée.',
            )}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Arborescence -----------------------------
 * Le laboratoire tient DEUX organigrammes (BIO24-A-ORG-01 fonctionnel et -02
 * nominatif) et le document le dit explicitement : le premier change quand
 * l'organisation change, le second « à chaque mouvement RH ». Les afficher
 * dans un seul écran à deux onglets — et non deux écrans — parce qu'on les lit
 * l'un contre l'autre : la question posée en audit est « cette case est-elle
 * tenue, et par qui ». */

const couleurTier = (key?: string) => ORG_TIERS.find((t) => t.key === key)?.color || 'var(--text-muted)';

/** Étiquette de champ dans les formulaires d'édition — même graisse et même
 *  casse que partout ailleurs dans l'application (cf. `lbl` des écrans NC/RH). */
const etiquetteChamp: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};

/** Noeud générique d'arbre, commun aux deux organigrammes. */
interface Noeud { id: string; parent: string | null; lien: 'hierarchique' | 'fonctionnel' }

/**
 * Racines = les noeuds sans parent, PLUS ceux dont le parent ne résout pas.
 * Sans ce second cas, une donnée abîmée (parent supprimé à la main en base)
 * ferait disparaître toute une branche de l'écran, sans message : l'arbre ne se
 * parcourt qu'à partir de ses racines.
 */
function racinesDe<T extends Noeud>(list: T[]): T[] {
  const ids = new Set(list.map((n) => n.id));
  return list.filter((n) => !n.parent || !ids.has(n.parent));
}

const enfantsDe = <T extends Noeud>(list: T[], id: string): T[] => list.filter((n) => n.parent === id);

/** `cible` est-elle dans la descendance de `ancetre` ? Garde anti-cycle du
 *  glisser-déposer : déposer un chef sous son propre subordonné produirait un
 *  arbre qui se parcourt à l'infini. */
function estDescendant<T extends Noeud>(list: T[], ancetre: string, cible: string): boolean {
  const vus = new Set<string>();
  const pile = [ancetre];
  while (pile.length) {
    const cur = pile.pop() as string;
    if (vus.has(cur)) continue;
    vus.add(cur);
    for (const e of enfantsDe(list, cur)) {
      if (e.id === cible) return true;
      pile.push(e.id);
    }
  }
  return false;
}

/** Trait de rattachement : plein si hiérarchique, pointillé si fonctionnel —
 *  c'est la légende du document, et l'indépendance de la RQ en dépend. */
const styleTrait = (lien: 'hierarchique' | 'fonctionnel') =>
  (lien === 'fonctionnel' ? '2px dashed var(--text-faint)' : '2px solid var(--border-strong)');

/**
 * Sens de lecture de l'arbre.
 *
 * `vertical` — la disposition classique : le parent au-dessus, la fratrie côte
 * à côte. Elle ne tient QUE si la fratrie est courte. Ici huit cases dépendent
 * de la Direction et sept du Coordinateur Technique : l'arbre faisait trois
 * fois la largeur de l'écran, et le réduire assez pour le voir en entier le
 * rendait illisible (35 %).
 *
 * `horizontal` — la profondeur va vers la DROITE, la fratrie s'empile vers le
 * bas. La largeur ne dépend alors plus que du nombre de niveaux (quatre ici,
 * et un organigramme en compte rarement plus), tandis que l'effectif pousse
 * vers le bas — c'est-à-dire dans le sens où une page défile naturellement.
 */
type SensArbre = 'vertical' | 'horizontal';

/** Tige entre une carte et la fratrie qu'elle encadre. */
function Tige({ lien, sens }: { lien: 'hierarchique' | 'fonctionnel'; sens: SensArbre }) {
  return sens === 'horizontal'
    ? <div aria-hidden style={{ width: 10, height: 0, borderTop: styleTrait(lien), alignSelf: 'center' }} />
    : <div aria-hidden style={{ width: 0, height: 10, margin: '0 auto', borderInlineStart: styleTrait(lien) }} />;
}

/**
 * Connecteur d'UN enfant vers son parent : une demi-barre le long de la
 * fratrie, plus le segment qui rejoint l'enfant.
 *
 * Une simple bordure sur la rangée d'enfants — ce qu'il y avait — trace un
 * trait sur TOUTE la largeur : il dépasse avant le premier enfant et après le
 * dernier, en direction de branches auxquelles il ne mène pas. Et il impose un
 * seul style à toute la fratrie, alors que le rattachement de la RQ est le seul
 * en pointillé.
 *
 * Ici chaque enfant dessine SA moitié de barre — rien avant s'il est le
 * premier, rien après s'il est le dernier — et son propre segment, avec son
 * propre trait. Enfant unique : les deux moitiés sont nulles, il ne reste que
 * le segment.
 *
 * En mode horizontal, `alignSelf: stretch` fait courir la barre sur toute la
 * hauteur du BLOC de l'enfant (sa carte et toute sa descendance) ; le segment
 * part de 50 %, c'est-à-dire exactement là où se trouve la carte, puisque
 * chaque niveau centre sa carte sur son propre bloc (`alignItems: center`).
 */
function Connecteur({ lien, premier, dernier, sens }: {
  lien: 'hierarchique' | 'fonctionnel'; premier: boolean; dernier: boolean; sens: SensArbre;
}) {
  const trait = styleTrait(lien);
  if (sens === 'horizontal') {
    return (
      <div aria-hidden style={{ position: 'relative', width: 18, alignSelf: 'stretch' }}>
        <div style={{
          position: 'absolute', insetInlineStart: 0, borderInlineStart: trait,
          top: premier ? '50%' : 0,
          bottom: dernier ? '50%' : 0,
        }} />
        <div style={{ position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0, top: '50%', borderTop: trait }} />
      </div>
    );
  }
  return (
    <div aria-hidden style={{ position: 'relative', height: 16, width: '100%' }}>
      <div style={{
        position: 'absolute', top: 0, borderTop: trait,
        insetInlineStart: premier ? '50%' : 0,
        insetInlineEnd: dernier ? '50%' : 0,
      }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, insetInlineStart: '50%', borderInlineStart: trait }} />
    </div>
  );
}

/** Bouton de repli d'une branche — le compte dit ce qu'on cache. */
function Replier({ n, replie, onBascule }: { n: number; replie: boolean; onBascule: () => void }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onBascule(); }}
      title={replie ? `Déplier ${n} branche(s)` : 'Replier cette branche'}
      aria-label={replie ? `Déplier ${n} branche(s)` : 'Replier cette branche'}
      aria-expanded={!replie}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
        minWidth: 22, height: 18, padding: '0 5px', cursor: 'pointer',
        border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-pill)',
        background: replie ? 'var(--navy-900)' : 'var(--surface-card)', color: replie ? 'var(--surface-card)' : 'var(--text-muted)',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, lineHeight: 1,
      }}>
      {replie ? `+${n}` : '−'}
    </button>
  );
}


/** Ce dont une vignette a besoin. Rassemblé en un objet unique pour que
 *  `Branche`, purement récursive, n'ait qu'une chose à faire suivre. */
interface CtxArbre {
  edit: boolean;
  /** La couleur CHOISIE d'une personne (`h202`), ou `null`. Passée par le
   *  contexte plutôt que lue dans chaque carte : `Carte` est rendue des
   *  dizaines de fois, et un accès au magasin par carte multiplierait les
   *  abonnements pour une donnée qui ne change qu'à l'enregistrement. */
  couleurPersonne: (orgId: string) => string | null;
  isRQ: boolean;
  moi: string;
  affiches: GedUser[];
  arbre: Noeud[];
  parId: Map<string, GedUser>;
  porte: string | null;
  survole: string | null;
  setPorte: (v: string | null) => void;
  setSurvole: (f: (s: string | null) => string | null) => void;
  poser: (v: string | null) => void;
  rattacher: (qui: string, vers: string | null) => void;
  onOuvrir: (id: string) => void;
  /** Ouvre le dossier SUR sa section « Compte de connexion » — le clic sur la
   *  pastille du compte (03/10/2026). */
  onOuvrirCompte: (id: string) => void;
  /** Personnes correspondant à la recherche — SURLIGNÉES, jamais filtrées :
   *  retirer les non-correspondants couperait les branches et l'organigramme
   *  ne voudrait plus rien dire. */
  trouve: Set<string>;
  sens: SensArbre;
  /** Branches repliées, par identifiant du parent. */
  replies: Set<string>;
  basculerRepli: (id: string) => void;
}

/**
 * Carte d'une personne — même vignette en consultation et en édition, seuls le
 * glissement et les commandes de rattachement s'ajoutent.
 *
 * Définie AU NIVEAU DU MODULE, et non dans le corps de `Organigramme` : le
 * survol pendant un glissement met l'état à jour à chaque `dragover`. Avec un
 * composant recréé à chaque rendu, React voit un type neuf, démonte puis
 * remonte toutes les cartes — et le navigateur interrompt le glissement en
 * cours, l'élément saisi ayant disparu. Le geste échouait une fois sur deux
 * sans qu'aucune erreur ne le signale.
 */
/**
 * LA PASTILLE DU COMPTE DE CONNEXION (03/10/2026, demande RQ).
 *
 * Posée en coin, en position ABSOLUE : elle n'ajoute aucune hauteur à la carte
 * — le tracé serré ESTIME les hauteurs avant de rendre, une ligne de plus
 * aurait fait chevaucher les cartes.
 *
 * Deux enveloppes, comme la carte elle-même :
 *  · `onOuvrir` fourni (tracé classique) → un vrai bouton. ⚠️ Le clic, l'appui
 *    et le glisser S'ARRÊTENT au bouton : sans cela, cliquer la pastille
 *    ouvrirait AUSSI le dossier par le clic de la carte (sur la mauvaise
 *    section), et l'appui démarrerait un glisser-déposer en mode édition ;
 *  · sans `onOuvrir` (tracé serré, rendu en HTML statique) → un simple repère
 *    `data-zone="compte"`, que la délégation de `ArbreOrganigramme` reconnaît.
 *
 * Sans `etatCompte` (lecteur non autorisé, moteur antérieur) : RIEN — on
 * n'affirme pas un état qu'on n'a pas lu. Masquée à l'impression : le
 * document d'organisation n'a pas à porter l'état des comptes.
 */
function PastilleCompte({ u, onOuvrir }: { u: GedUser; onOuvrir?: (id: string) => void }) {
  const p = pastilleCompte(u.etatCompte);
  if (!p) return null;
  const titre = titrePastille(u.etatCompte, u.nom || '');
  const rond = (
    <span aria-hidden style={{
      display: 'block', width: 9, height: 9, borderRadius: '50%', boxSizing: 'border-box',
      background: p.plein ? p.couleur : 'transparent', border: `1.5px solid ${p.couleur}`,
    }} />
  );
  const place: React.CSSProperties = {
    position: 'absolute', top: 5, insetInlineEnd: 5, width: 18, height: 18,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%', padding: 0, background: 'transparent', border: 'none',
  };
  if (!onOuvrir) {
    return <span data-zone="compte" data-pastille-compte="" role="img" aria-label={p.libelle} title={titre}
      style={{ ...place, cursor: 'pointer' }}>{rond}</span>;
  }
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <button type="button" data-pastille-compte="" aria-label={titre} title={titre}
      draggable={false}
      onClick={(e) => { e.stopPropagation(); onOuvrir(u.id); }}
      onMouseDown={stop} onPointerDown={stop}
      onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
      style={{ ...place, cursor: 'pointer' }}>
      {rond}
    </button>
  );
}

/**
 * LE CONTENU D'UNE CARTE — un seul gabarit, deux enveloppes (Lot 1, 03/09/2026).
 *
 * ⚠️ EXTRAIT PARCE QUE `d3-org-chart` REND DE L'HTML. Son `nodeContent` doit
 * retourner une CHAÎNE, produite ici par `renderToStaticMarkup` : aucun
 * gestionnaire React ne survit à ce passage. Le tracé serré a donc besoin du
 * VISUEL sans l'interaction, et le tracé classique du visuel AVEC.
 *
 * Le découper est ce qui évite de le peindre deux fois — la spec l'interdit
 * explicitement (« ne pas dupliquer le design des cartes : un seul gabarit par
 * type »), et pour une raison qu'on a déjà payée ailleurs : deux gabarits
 * divergent, et c'est celui qu'on regarde le moins qui devient faux.
 */
function ContenuCarte({ u, couleurPersonne }: {
  u: GedUser; couleurPersonne: (orgId: string) => string | null;
}) {
  return (
    <>
      {/* ⚠️ LA MÊME PASTILLE QU'AU PLANNING (Lot 2, 02/09/2026). « Une personne
          ne doit jamais changer d'apparence d'un écran à l'autre » : la couleur
          choisie sur sa fiche vaut ici comme au planning, et la même formule la
          rend — donc le même contraste garanti.
          ⚠️ SANS couleur choisie, on garde EXACTEMENT le rond plein du niveau
          hiérarchique. C'est l'apparence actuelle de tout le monde, et rien ne
          doit bouger tant que personne n'a rien réglé. */}
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        ...(() => {
          /* LA MÊME COULEUR QUE LE PLANNING ET LA FICHE (02/10/2026) : la
             teinte EFFECTIVE — celle choisie, sinon le hachage de l'identifiant
             (`pastilleDe`). Le rond plein du niveau n'est plus utilisé : il
             montrait une couleur que ni le planning ni la fiche ne montraient. */
          const p = pastilleDe(u.id, couleurPersonne(u.id));
          return { background: p.background, color: p.color, border: `1.5px solid ${p.borderColor}` };
        })(),
      }}>{u.initiales || initials(u.nom)}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginTop: 3, lineHeight: 1.25 }}>{u.nom || <span style={{ color: 'var(--text-faint)' }}>Sans nom</span>}</div>
      {!estAffectable(u) && (
        <div style={{ display: 'inline-block', marginTop: 4, fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--gray-100)', color: 'var(--text-muted)' }}>
          {libelleIndisponible(u)}
        </div>
      )}
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.3 }}>{u.fonction}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--color-primary-active)', marginTop: 2 }}>{(u.roles || []).map((r) => GED_ROLES[r]?.short || r).join(' · ')}</div>
    </>
  );
}

function Carte({ u, ctx }: { u: GedUser; ctx: CtxArbre }) {
  const couleur = couleurTier(tierOfUser(u));
  const { couleurPersonne } = ctx;
  /* ⚠️ REFUS SIGNALÉ DÈS LE GLISSÉ (Lot 4, spec §Lot 4) : pas seulement au
     dépôt. Dès que `ctx.porte` est posé (`onDragStart`), TOUTE carte qui est la
     personne portée ou l'une de ses descendantes se lit comme une cible
     invalide — et se réévalue à chaque changement de `ctx.porte`, sans état
     dédié : c'est une simple lecture de `ctx.arbre`, déjà disponible ici. */
  const invalideCommeCible = !!ctx.porte
    && (ctx.porte === u.id || estDescendant(ctx.arbre, ctx.porte, u.id));
  const cible = ctx.survole === u.id && ctx.porte && !invalideCommeCible;
  const vise = ctx.trouve.has(u.id);
  return (
    <div
      data-org-trouve={vise ? '1' : undefined}
      draggable={ctx.edit}
      onDragStart={(e) => { ctx.setPorte(u.id); e.dataTransfer.effectAllowed = 'move'; }}
      onDragEnd={() => { ctx.setPorte(null); ctx.poser(null); }}
      onDragOver={(e) => { if (ctx.edit && ctx.porte && !invalideCommeCible) { e.preventDefault(); ctx.poser(u.id); } }}
      onDragLeave={() => ctx.setSurvole((s) => (s === u.id ? null : s))}
      onDrop={(e) => { e.preventDefault(); if (ctx.porte && !invalideCommeCible) ctx.rattacher(ctx.porte, u.id); ctx.setPorte(null); ctx.poser(null); }}
      /* ⚠️ LE CLIC OUVRE LA FICHE MÊME EN ÉDITION (Lot 5, spec §5.1) : « au clic
         sur une carte en mode édition, ouvrir un panneau latéral ». Aucun
         conflit avec le glisser : un `dragstart` suivi d'un `drop` ne produit
         pas de `click` (comportement natif du navigateur), donc déplacer une
         carte n'ouvre jamais la fiche par erreur. */
      onClick={() => ctx.onOuvrir(u.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ctx.onOuvrir(u.id); } }}
      role="button"
      tabIndex={0}
      title={`Ouvrir le dossier de ${u.nom || u.id}`}
      style={{
        ...card, padding: '7px 10px', minWidth: 128, maxWidth: 170, textAlign: 'center',
        borderTop: `3px solid ${couleur}`, position: 'relative',
        cursor: ctx.edit ? 'grab' : 'pointer',
        opacity: invalideCommeCible ? 0.35 : 1,
        // Le glissement garde la priorité : pendant un rattachement, la cible
        // survolée doit rester le seul repère visible.
        outline: cible ? '2px solid var(--color-primary)' : vise ? '2px solid var(--amber-500)' : 'none',
        outlineOffset: 2,
        boxShadow: vise && !cible ? '0 0 0 5px #FCF2E1' : undefined,
      }}
    >
      <PastilleCompte u={u} onOuvrir={ctx.onOuvrirCompte} />
      <ContenuCarte u={u} couleurPersonne={couleurPersonne} />
      {/* Le glisser-déposer ne peut pas être le SEUL moyen de rattacher : il est
          inaccessible au clavier et pénible sur écran tactile. */}
      {ctx.edit && (
        <Selecteur
          value={u.responsable || ''}
          aria-label={`Responsable de ${u.nom || u.id}`}
          onChange={(v) => ctx.rattacher(u.id, v || null)}
          style={{ ...inputStyle, marginTop: 8, fontSize: 12.5, padding: '5px 7px' }}
        >
          <option value="">— Rattaché à personne (racine)</option>
          {ctx.affiches
            .filter((o) => o.id !== u.id && !estDescendant(ctx.arbre, u.id, o.id))
            .map((o) => <option key={o.id} value={o.id}>{o.nom || o.id}</option>)}
        </Selecteur>
      )}
      {/* Le transfert de responsabilités a quitté la vignette pour la fiche :
          répété sur dix-huit cartes, il doublait leur hauteur et donnait au
          geste le plus lourd du SMQ l'apparence d'une commande courante. */}
    </div>
  );
}

/**
 * Cases tenues par une personne — l'appartenance à PLUSIEURS équipes (GED-6).
 *
 * Le modèle l'accepte depuis la bascule RH (`fonctionIds`) et les données
 * serveur l'utilisent — quatre personnes tiennent déjà deux cases. Mais AUCUNE
 * interface ne l'écrivait : l'écran des fonctions renvoyait « à la fiche de la
 * personne », où le champ est en lecture seule. On promettait un geste qui
 * n'existait nulle part, ce qui est pire que de ne rien promettre : la case
 * paraît réglable, on croit l'avoir réglée, rien ne bouge.
 *
 * ⚠️ Ce que ce composant écrit va dans le BROUILLON `gedUsers`, que
 * l'enregistrement envoie à `ged-setting`. Là, `synchroniserDepuisVue` écrit
 * `rh-personnel.fonctionIds` — la SOURCE — puis `vueOrganigramme` recompose la
 * vue depuis elle. C'est ce sens d'écriture, et lui seul, qui fait que le
 * rattachement SURVIT à un recalcul RH. Une écriture qui poserait `gedUsers`
 * directement serait effacée au premier recalcul, sans erreur ni trace (cf.
 * l'en-tête de `backend/src/rh-org.ts`, et SEC-7).
 *
 * Des bascules et non une liste à choix multiples : le `<select multiple>` du
 * navigateur exige la touche Ctrl pour cumuler, ce que personne ne devine — et
 * un clic simple y REMPLACE la sélection. Sur un écran dont tout l'objet est le
 * cumul, c'est le seul geste qu'il ne faut pas rendre accidentel.
 */
function PostesTenus({ u, fonctions, onChange }: {
  u: GedUser; fonctions: OrgFonction[]; onChange: (ids: string[]) => void;
}) {
  if (!fonctions.length) return null;
  const tenues = u.fonctionIds || [];
  /* ⚠️ EN LISTE DÉROULANTE DEPUIS LE 12/09/2026, comme sur la fiche de la
     personne — c'est le même champ, vu d'un autre écran, et il portait le même
     défaut : les huit cases affichées en permanence, dont sept qu'on ne tient
     pas. Sur un organigramme où l'on édite plusieurs personnes à la suite, huit
     pastilles par ligne remplissent la page de ce qu'on n'a pas choisi.
     ⚠️ CE QUI EST PERDU : la teinte du niveau hiérarchique (`couleurTier`) sur
     chaque pastille. Elle ne servait qu'à colorer les options NON tenues —
     celles qui ont disparu. Les cases tenues, elles, se lisent dans l'arbre
     juste au-dessus, avec leur couleur.
     ⚠️ Et le socle est le MÊME que celui de la fiche : deux interfaces pour
     écrire `fonctionIds` auraient divergé, comme elles l'avaient déjà fait
     avant que la fiche ne sache l'écrire du tout. */
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', paddingInlineStart: 2 }}>
      <span style={{ ...etiquetteChamp, marginBottom: 0, whiteSpace: 'nowrap' }}>
        Postes tenus{tenues.length > 1 ? ` (${tenues.length})` : ''}
      </span>
      <div style={{ flex: '1 1 260px', minWidth: 220 }}>
        <SelecteurPastilles mono={false} modifiable unite="case"
          ariaLabel={`Cases de l’organigramme tenues par ${u.nom || u.id}`}
          placeholder="Choisir une case…"
          vide="Aucune — la personne ne tient aucune case de l’organigramme fonctionnel."
          options={fonctions.map((f) => ({ documentId: f.id, code: f.label, nom: f.label }))}
          choisis={tenues}
          onChange={onChange} />
      </div>
    </div>
  );
}

/** Rendu récursif d'une branche. `profondeur` borne la descente : même avec la
 *  garde anti-cycle, une donnée héritée abîmée ne doit pas figer l'écran. */
function Branche({ id, ctx, profondeur = 0 }: { id: string; ctx: CtxArbre; profondeur?: number }) {
  const u = ctx.parId.get(id);
  if (!u || profondeur > 12) return null;
  const enfants = enfantsDe(ctx.arbre, id);
  const h = ctx.sens === 'horizontal';
  const replie = ctx.replies.has(id);
  return (
    // Horizontal : la carte et le bloc de ses enfants sur une même LIGNE, et
    // `alignItems: center` place la carte au milieu vertical de sa descendance
    // — c'est ce qui fait tomber les connecteurs pile en face d'elle.
    <div style={{ display: 'flex', flexDirection: h ? 'row' : 'column', alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <Carte u={u} ctx={ctx} />
        {enfants.length > 0 && (
          <Replier n={enfants.length} replie={replie} onBascule={() => ctx.basculerRepli(id)} />
        )}
      </div>
      {enfants.length > 0 && !replie && (
        <>
          <Tige lien="hierarchique" sens={ctx.sens} />
          {/* `nowrap` en vertical : une fratrie qui passe à la ligne se retrouve
              sous un autre parent que le sien — le trait ne relie plus ce qu'il
              prétend relier. Le conteneur défile, c'est lui qui absorbe. */}
          <div style={{
            display: 'flex', gap: h ? 6 : 8,
            flexDirection: h ? 'column' : 'row',
            alignItems: h ? 'flex-start' : 'flex-start',
            justifyContent: 'center', flexWrap: 'nowrap',
          }}>
            {enfants.map((e, i) => (
              <div key={e.id} style={{ display: 'flex', flexDirection: h ? 'row' : 'column', alignItems: 'center' }}>
                <Connecteur lien={ctx.parId.get(e.id)?.lien || 'hierarchique'}
                  premier={i === 0} dernier={i === enfants.length - 1} sens={ctx.sens} />
                <Branche id={e.id} ctx={ctx} profondeur={profondeur + 1} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------- Organigramme & co --------------------------- */
export function Organigramme() {
  const { settings, saveSettings, session, smq, showToast, rhPersonnel, refresh, naviguerVers } = useGed();
  const isRQ = session.roles.includes('rq');
  const [transfert, setTransfert] = useState<string | null>(null);
  /** Personne dont la fiche est ouverte — un clic sur sa vignette. */
  const [dossier, setDossier] = useState<string | null>(null);
  /** Section du dossier à amener sous les yeux à l'ouverture — `compte` quand on
   *  vient de la pastille du compte. Remise à `null` à chaque autre ouverture :
   *  une navigation aux flèches ne doit pas sauter, elle, au compte. */
  /* ⚠️ `dossierSection` A DISPARU avec la fiche (06/10/2026). Il amenait sous
     les yeux la section « Compte de connexion », en bas d'un panneau long, quand
     on venait de la pastille du compte. Le résumé tient en un écran et porte
     l'état du compte dans son en-tête : il n'y a plus de section à aller
     chercher, donc plus rien à faire défiler. */
  const ouvrirDossier = (id: string | null) => setDossier(id);
  /* OUVERTURE DEMANDÉE PAR UN AUTRE ÉCRAN (03/10/2026). L'intégration RH et
     l'annuaire des comptes (Paramétrage → Utilisateurs) posent
     `selectedPersonne` puis naviguent ici : rien ne la lisait, et « Ouvrir la
     fiche » déposait sur l'organigramme sans rien ouvrir. Consommée une fois,
     puis effacée — sinon revenir sur l'écran rouvrirait la même fiche. */
  const personneDemandee = useGed((s) => s.selectedPersonne);
  const selectionner = useGed((s) => s.select);
  useEffect(() => {
    if (!personneDemandee) return;
    setDossier(personneDemandee);
    selectionner('selectedPersonne', null);
  }, [personneDemandee, selectionner]);
  /** Formulaire d'arrivée — créer quelqu'un ne passait que par un script SQL. */
  const [arrivee, setArrivee] = useState(false);
  // Une référence effacée dans le paramétrage retombe sur celle du document
  // d'origine plutôt que sur un onglet sans référence.
  const orgRefs = {
    fonctionnel: settings?.codification?.orgRefs?.fonctionnel?.trim() || ORG_REFS_DEFAUT.fonctionnel,
    nominatif: settings?.codification?.orgRefs?.nominatif?.trim() || ORG_REFS_DEFAUT.nominatif,
  };
  /* Un TROISIÈME onglet (26/08/2026) : les fiches de FONCTION. Elles vivent
     ici et non dans un écran à part parce qu'on les lit contre l'organigramme —
     la question posée est « qu'est-ce que ce métier fait », juste à côté de
     « qui le tient ». Ce n'est pas un troisième organigramme : c'est ce que les
     cases DÉCRIVENT. */
  const [vue, setVue] = useState<'nominatif' | 'fonctionnel' | 'fonctions'>('nominatif');
  const [edit, setEdit] = useState(false);
  /* LE PERSONNEL LIT LA DERNIÈRE VERSION APPROUVÉE (04/10/2026). Tant qu'une
     révision est ouverte, le RQ et la RH voient le brouillon qu'ils préparent ;
     les autres voient la copie figée à la dernière approbation. Voir
     `lib/org-affiche.ts` et `backend/src/org-instantane.ts`. */
  const circuitNominatif = settings?.codification?.orgCircuit?.nominatif || CIRCUIT_ORG_DEFAUT;
  const circuitFonctionnel = settings?.codification?.orgCircuit?.fonctionnel || CIRCUIT_ORG_DEFAUT;
  const voitBrouillon = isRQ || session.roles.includes('rh');
  const affN = organigrammeAffiche({
    vivant: settings?.gedUsers || [],
    instantane: settings?.codification?.orgApprouve?.nominatif || null,
    etat: circuitNominatif.etat, voitBrouillon, enEdition: edit,
  });
  /* Le FONCTIONNEL (04/10/2026) : ses CASES et leurs titulaires viennent de la
     même source — la copie approuvée ou le brouillon, jamais un mélange des
     deux (des titulaires d'aujourd'hui sur des cases d'hier). */
  const copieF = settings?.codification?.orgApprouve?.fonctionnel || null;
  const affF = organigrammeAffiche({
    vivant: settings?.gedUsers || [],
    instantane: copieF,
    etat: circuitFonctionnel.etat, voitBrouillon, enEdition: edit,
  });
  const fonctions: OrgFonction[] = affF.source === 'approuvee'
    ? (copieF?.fonctions || []) : (settings?.orgFonctions || []);
  const vueF = vue === 'fonctionnel';
  const sourceOrg = vueF ? affF.source : affN.source;
  const copieVue = vueF ? copieF : settings?.codification?.orgApprouve?.nominatif;
  const users = personnelReel(vueF ? affF.personnes : affN.personnes);
  const [draft, setDraft] = useState<GedUser[]>([]);
  const [saving, setSaving] = useState(false);
  /** Brouillon des CASES (organigramme fonctionnel), distinct de `draft` qui
   *  porte les personnes : les deux organigrammes s'éditent séparément, comme
   *  ils se lisent séparément. */
  const [draftF, setDraftF] = useState<OrgFonction[]>([]);
  /** Échelle d'affichage. `null` = ajustée automatiquement à la largeur du
   *  cadre ; un nombre = choix explicite de l'utilisateur, qu'un
   *  redimensionnement ne doit pas écraser. */
  /* Le TRACÉ : serré (d3-flextree) ou classique (le tracé maison). Mémorisé par
     utilisateur — c'est un choix de lecture, pas un réglage du laboratoire. */
  const [trace, setTrace] = useState<'serre' | 'classique'>(() => {
    try { return localStorage.getItem('bio24.org.trace') === 'classique' ? 'classique' : 'serre'; } catch { return 'serre'; }
  });
  const choisirTrace = (t: 'serre' | 'classique') => {
    setTrace(t);
    try { localStorage.setItem('bio24.org.trace', t); } catch { /* navigation privée */ }
  };
  /** Les commandes du tracé serré (ajuster, zoom, replier), rendues par lui. */
  const poignees = React.useRef<PoigneesArbre | null>(null);
  /** La confirmation en attente d'un re-rattachement (Lot 4) — `null` tant
   *  qu'aucun geste n'est en cours. */
  const [confirmRatt, setConfirmRatt] = useState<{ qui: string; vers: string | null; nomQui: string; nomVers: string } | null>(null);
  const [motifRatt, setMotifRatt] = useState('');
  const [envoiRatt, setEnvoiRatt] = useState(false);

  /**
   * L'ANCIEN FORMULAIRE DEVIENT UNE « VUE TABLEAU » (Lot 5, spec §5.5).
   *
   * ⚠️ NE PAS SUPPRIMER LE FORMULAIRE : la spec le dit explicitement — il reste
   * supérieur pour les tâches de masse (vérifier d'un coup d'œil toutes les
   * initiales, repérer un doublon). L'arbre devient seulement le mode d'édition
   * PAR DÉFAUT ; le panneau ouvert par clic sur une carte (`ResumePersonne`)
   * sert, lui, à CONSULTER au cas par cas — l'écriture du dossier a quitté cet
   * écran le 06/10/2026.
   */
  const [vueEdition, setVueEdition] = useState<'arbre' | 'tableau'>('arbre');
  const [zoom, setZoom] = useState<number | null>(null);
  const [zoomAuto, setZoomAuto] = useState(1);
  /** Sens de lecture. `horizontal` par DÉFAUT : avec huit cases sous la
   *  Direction, la disposition classique demandait 35 % de réduction pour
   *  tenir — soit un arbre entier mais illisible. */
  const [sens, setSens] = useState<SensArbre>('horizontal');
  const [replies, setReplies] = useState<Set<string>>(new Set());
  const basculerRepli = React.useCallback((id: string) => setReplies((s) => {
    const n = new Set(s);
    if (!n.delete(id)) n.add(id);
    return n;
  }), []);
  /**
   * L'arbre est plus large que le cadre dès une quinzaine de personnes, et
   * chaque parent est centré au-dessus de ses enfants : à `scrollLeft = 0` on
   * ouvre donc l'écran sur une branche quelconque, jamais sur la direction. On
   * centre le défilement une fois l'arbre mesuré.
   */
  const cadre = React.useRef<HTMLDivElement | null>(null);
  /** Personne saisie par la souris, le temps du glissement. */
  /* A9 — la case dont on regarde la fiche DÉDUITE. Elle ne s'ouvre que pour
     les cases SANS document : là où la fiche existe, c'est elle qu'on ouvre,
     et un panneau d'éléments déduits ferait écran au document maîtrisé. */
  const [ficheCase, setFicheCase] = useState<string | null>(null);
  const [porte, setPorte] = useState<string | null>(null);
  const [survole, setSurvole] = useState<string | null>(null);
  const [q, setQ] = useState('');
  // Une entrée dont l'identifiant est un vrai compte (username, ≠ `u_…`) ne se
  // supprime PAS de l'organigramme : son historique la référence. Seules les
  // lignes ajoutées à la main (annotations `u_…`, sans compte) se retirent.
  const estCompte = (id: string) => !!id && !id.startsWith('u_');

  /* Les versions des réglages AU MOMENT où le brouillon a été pris (14/10/2026) :
     l'enregistrement les renvoie, et le serveur refuse si `gedUsers` ou
     `orgFonctions` a bougé depuis — un rôle attribué ailleurs n'est plus
     effacé par un arbre resté ouvert. */
  const versionsBase = React.useRef<Record<string, string> | undefined>(undefined);
  const startEdit = () => {
    versionsBase.current = settings?.versions;
    setDraft(JSON.parse(JSON.stringify(settings?.gedUsers || [])));
    setDraftF(JSON.parse(JSON.stringify(settings?.orgFonctions || [])));
    setEdit(true);
  };

  /* ── `resyncDraftDepuisMagasin` A ÉTÉ RETIRÉ LE 06/10/2026 ────────────────
   *
   * Il reclonait `draft` depuis le magasin après un enregistrement fait dans la
   * fiche personnelle ouverte par-dessus l'arbre (Lot 5, 04/09/2026) : sans lui,
   * le brouillon de l'arbre gardait les valeurs d'avant l'écriture.
   *
   * Ce besoin n'existe plus : le panneau ouvert depuis une carte est
   * `ResumePersonne`, qui n'écrit RIEN. Plus aucune écriture ne part de cet
   * écran pendant qu'un brouillon y est ouvert, donc plus rien à resynchroniser.
   * Le garder appelé « au cas où » aurait laissé croire le contraire à la
   * relecture ; le garder non appelé est du code mort. */

  /* ---- Édition des CASES de l'organigramme fonctionnel -------------------
   * Ces réglages n'existaient nulle part : les cases n'étaient écrites qu'au
   * démarrage du serveur, depuis `backend/src/org-bio24.ts`. Le laboratoire
   * ne pouvait donc ni renommer une case, ni en ajouter une, ni tracer un
   * rattachement en pointillé — alors que c'est précisément ce trait qui
   * matérialise l'indépendance du responsable qualité (§ ISO 15189 5.6). */
  const patchFonction = (id: string, p: Partial<OrgFonction>) =>
    setDraftF((d) => d.map((f) => (f.id === id ? { ...f, ...p } : f)));
  const addFonction = () => setDraftF((d) => [...d, {
    id: `f_${Date.now().toString(36)}`, label: '', tier: 'support',
    parent: d[0]?.id ?? null, lien: 'hierarchique',
  }]);
  const removeFonction = (id: string) => setDraftF((d) => d
    // Supprimer une case, c'est aussi remonter d'un cran celles qu'elle
    // coiffait : les laisser pointer vers une case disparue les ferait
    // basculer en racines et l'arbre se lirait de travers.
    .map((f) => (f.parent === id ? { ...f, parent: d.find((x) => x.id === id)?.parent ?? null } : f))
    .filter((f) => f.id !== id));
  const patchUser = (id: string, p: Partial<GedUser>) => setDraft((d) => d.map((u) => (u.id === id ? { ...u, ...p } : u)));
  /* `addUser` a été SUPPRIMÉ le 01/09/2026 avec le bouton qui l'appelait — voir
     la note au-dessus du paragraphe qui l'a remplacé, plus bas dans ce fichier.
     `removeUser` reste : il est le seul moyen de retirer une ligne `u_…` déjà
     posée par l'ancien bouton, et il n'y en a plus aucune en dev — mais on ne
     supprime pas l'échappatoire d'une base qu'on n'a pas sous les yeux. */
  const removeUser = (id: string) => setDraft((d) => d
    // Retirer une personne, c'est aussi détacher ceux qu'elle encadrait : sinon
    // ils gardent un responsable qui n'existe plus et leur branche entière
    // disparaît de l'écran au prochain rendu.
    .map((u) => (u.responsable === id ? { ...u, responsable: null } : u))
    .filter((u) => u.id !== id));

  /**
   * Rattachement par glisser-déposer : `qui` passe sous `vers` (`null` = racine).
   * Le serveur re-vérifie (`romprLesCycles`) — cette garde-ci est là pour que
   * le geste soit refusé À LA SOURCE, plutôt que corrigé en silence après coup.
   */
  /** Remise à plat des cases depuis le document de référence. Passe par le
   *  moteur (et non par `saveSettings`) : c'est lui qui détient le document,
   *  élague les titulaires devenus orphelins et journalise le geste. */
  const reinitialiserFonctions = () => smq(
    'ged/org/fonctions/reinitialiser', {},
    'Organigramme fonctionnel réinitialisé depuis le document de référence.',
  );

  /**
   * ── LE RE-RATTACHEMENT, GOUVERNÉ (Lot 4, 03/09/2026) ────────────────────
   * Le geste ne mute plus `draft` en silence : il OUVRE la confirmation
   * (`confirmRatt`) qui nomme les deux personnes et exige un motif — la
   * mutation elle-même n'a lieu qu'après que le serveur a accepté, dans
   * `confirmerRattachement`. Le contrôle de cycle reste ici, AVANT d'ouvrir
   * la boîte : inutile de faire saisir un motif pour un geste qui sera de
   * toute façon refusé.
   */
  const rattacher = (qui: string, vers: string | null) => {
    if (qui === vers) return;
    const arbre = draft.map((u) => ({ id: u.id, parent: u.responsable ?? null, lien: u.lien || 'hierarchique' }));
    if (vers && estDescendant(arbre, qui, vers)) {
      showToast('Rattachement impossible : cette personne encadre déjà, directement ou indirectement, celle sous laquelle vous la déposez.', 'erreur');
      return;
    }
    const personne = draft.find((u) => u.id === qui);
    const ancien = personne?.responsable || null;
    if (ancien === vers) return;
    setConfirmRatt({
      qui, vers,
      nomQui: nomDe(personne) || qui,
      nomVers: vers ? (draft.find((u) => u.id === vers)?.nom || vers) : 'la racine',
    });
    setMotifRatt('');
  };
  const confirmerRattachement = async () => {
    if (!confirmRatt || !motifRatt.trim()) return;
    setEnvoiRatt(true);
    const ok = await smq(
      'ged/rh/organigramme/rattacher',
      { qui: confirmRatt.qui, vers: confirmRatt.vers, motif: motifRatt.trim() },
      `${confirmRatt.nomQui} rattaché(e) à ${confirmRatt.nomVers}.`,
    );
    setEnvoiRatt(false);
    if (ok) {
      // La vue serveur revient par `refresh()` (dans `smq`), mais `draft` est un
      // CLONE local pris à l'entrée en édition : sans ce patch, la carte
      // resterait affichée à son ancienne place jusqu'à la sortie du mode
      // édition, alors que le serveur a déjà accepté le changement.
      setDraft((d) => d.map((u) => (u.id === confirmRatt.qui ? { ...u, responsable: confirmRatt.vers } : u)));
      // Ce rattachement a réécrit `gedUsers` côté serveur, et le brouillon le
      // porte désormais : la version de base avance avec lui, sans quoi notre
      // propre geste serait pris pour celui d'un autre.
      const fraiches = useGed.getState().settings?.versions;
      if (fraiches?.gedUsers) versionsBase.current = { ...(versionsBase.current || {}), gedUsers: fraiches.gedUsers };
      setConfirmRatt(null);
      setMotifRatt('');
    }
  };
  const saveOrg = async () => {
    setSaving(true);
    const ok = vue === 'fonctionnel'
      // Une case sans libellé n'est pas une case : elle s'afficherait comme un
      // rectangle vide que rien ne permet d'identifier ni de corriger.
      ? await saveSettings({ orgFonctions: draftF.filter((f) => f.label.trim()).map((f) => ({ ...f, label: f.label.trim() })) } as never, versionsBase.current)
      : await saveSettings({ gedUsers: draft.filter((u) => u.nom.trim()).map((u) => ({ ...u, nom: u.nom.trim(), initiales: (u.initiales || '').trim() })) } as never, versionsBase.current);
    setSaving(false);
    if (ok) setEdit(false);
  };
  // Le brouillon porte TOUT l'organigramme : quitter l'édition sans garde
  // effaçait d'un coup un remaniement complet. Même convention que la fenêtre
  // de paramétrage — comparaison au contenu servi, pas un simple drapeau, pour
  // ne pas alerter quand on est revenu de soi-même à l'état initial.
  const dirty = edit && (vue === 'fonctionnel'
    ? JSON.stringify(draftF) !== JSON.stringify(settings?.orgFonctions || [])
    : JSON.stringify(draft) !== JSON.stringify(settings?.gedUsers || []));
  const { demander, confirmation } = useConfirmation();
  const annuler = React.useCallback(async () => {
    if (dirty && !(await demander('Des modifications non enregistrées seront perdues. Quitter l’édition ?', { confirmer: 'Quitter sans enregistrer' }))) return;
    setEdit(false);
  }, [dirty, demander]);
  useEscape(edit && !saving, annuler);

  // Après chaque changement de vue ou d'effectif : recentrer. Les dépendances
  // couvrent le passage nominatif ↔ fonctionnel, l'entrée en édition et
  // l'ajout d'une personne — les trois cas qui changent la largeur de l'arbre.
  useEffect(() => {
    const centrer = () => {
      const el = cadre.current;
      if (!el) return;
      // En colonnes, la racine est déjà tout à gauche : la « centrer »
      // horizontalement la poussait hors du cadre, et l'écran s'ouvrait sur le
      // deuxième niveau — la direction restait invisible à gauche (constaté).
      // Reste à la cadrer VERTICALEMENT : chaque carte étant centrée sur sa
      // descendance, la racine se trouve au milieu d'un arbre de 1 300 px, donc
      // hors de vue à l'ouverture.
      if (sens === 'horizontal') {
        el.scrollLeft = 0;
        const carte = el.firstElementChild?.firstElementChild?.querySelector('div');
        if (carte) {
          const dansLeCadre = carte.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
          el.scrollTop = Math.max(0, dansLeCadre - el.clientHeight / 2 + carte.getBoundingClientRect().height / 2);
        }
        return;
      }
      // En arbre, on vise la PREMIÈRE racine, pas le milieu du cadre : l'arbre
      // est asymétrique (la branche du biologiste responsable pèse quinze
      // personnes, celle du co-directeur une seule), donc son centre
      // géométrique ne tombe pas sur la direction — c'est pourtant par là
      // qu'un organigramme se lit.
      const racine = el.firstElementChild?.firstElementChild?.firstElementChild;
      if (!racine) return;
      const cadreRect = el.getBoundingClientRect();
      const carte = racine.getBoundingClientRect();
      const ecart = (carte.left + carte.width / 2) - (cadreRect.left + el.clientWidth / 2);
      el.scrollLeft = Math.max(0, el.scrollLeft + ecart);
    };
    // Deux trames d'attente : mesurer dès l'effet donne des largeurs de cartes
    // provisoires (polices non encore appliquées), et le centrage tombe à côté
    // — constaté, l'écart était de 350 px.
    const t = requestAnimationFrame(() => requestAnimationFrame(centrer));
    window.addEventListener('resize', centrer);
    return () => { cancelAnimationFrame(t); window.removeEventListener('resize', centrer); };
  }, [vue, edit, users.length, fonctions.length, sens]);

  /**
   * Échelle d'ajustement à la largeur.
   *
   * L'arbre est bien plus large que le cadre (dix-huit cases sur cinq niveaux) :
   * il ne se lisait qu'au défilement horizontal, morceau par morceau, ce qui
   * interdit précisément ce qu'on demande à un organigramme — voir d'un coup
   * qui dépend de qui. On mesure donc la largeur RÉELLE de l'arbre et on en
   * déduit l'échelle qui le fait tenir.
   *
   * Mesure sur `getBoundingClientRect()`, seule mesure qui rende des pixels
   * RÉELLEMENT affichés : `scrollWidth` et consorts ignorent la réduction en
   * cours, et l'échelle se serait recalculée sur une largeur qui n'est pas
   * celle qu'on voit. On divise donc par l'échelle appliquée pour retrouver la
   * largeur naturelle de l'arbre, invariant sur lequel raisonner.
   *
   * Réduction par la propriété `zoom` et non par `transform: scale()` : une
   * transformation ne change pas la place PRISE par l'élément. Le conteneur
   * défilant continuait de réserver la largeur d'origine — l'arbre rapetissait
   * dans un coin, suivi d'une immense zone vide.
   */
  const interieur = React.useRef<HTMLDivElement | null>(null);
  const echelle = zoom ?? zoomAuto;
  useEffect(() => {
    const mesurer = () => {
      const cad = cadre.current;
      const dedans = interieur.current;
      if (!cad || !dedans) return;
      const largeurArbre = dedans.getBoundingClientRect().width / echelle;
      if (!largeurArbre) return;
      // Jamais d'agrandissement : un petit arbre étiré au ras du cadre serait
      // grotesque. Et jamais sous 35 % — en dessous plus rien ne se lit, mieux
      // vaut alors laisser le défilement finir le travail.
      setZoomAuto(Math.max(0.35, Math.min(1, cad.clientWidth / largeurArbre)));
    };
    const t = requestAnimationFrame(() => requestAnimationFrame(mesurer));
    window.addEventListener('resize', mesurer);
    return () => { cancelAnimationFrame(t); window.removeEventListener('resize', mesurer); };
    // `fonctions` / `draftF` et non `fonctionsAffichees` : ce dernier est
    // déclaré plus bas, et le citer ici le lirait dans sa zone morte.
    // `sens` et `replies` changent la largeur de l'arbre autant qu'un
    // changement d'effectif : sans eux, l'échelle resterait celle de la
    // disposition précédente.
  }, [vue, edit, users.length, fonctions.length, draftF.length, echelle, sens, replies]);

  // Amener la première correspondance sous les yeux. L'arbre déborde largement
  // du cadre : surligner une carte restée hors champ ne servirait à rien.
  // `block: 'nearest'` pour ne pas faire sauter la page entière verticalement.
  useEffect(() => {
    if (!q.trim()) return;
    const t = requestAnimationFrame(() => {
      cadre.current?.querySelector('[data-org-trouve="1"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
    return () => cancelAnimationFrame(t);
  }, [q]);

  // Source de l'arbre affiché : le brouillon en édition, les données servies
  // sinon. Une seule expression, pour que le glisser-déposer se voie
  // immédiatement sans dupliquer le rendu.
  const affiches = edit ? personnelReel(draft) : users;
  const arbre = affiches.map((u) => ({ id: u.id, parent: u.responsable ?? null, lien: u.lien || 'hierarchique' }));
  const parId = new Map(affiches.map((u) => [u.id, u]));

  /**
   * FLÈCHES HAUT/BAS DANS LA FRATRIE (Lot 5, spec §5.1).
   *
   * ⚠️ ÉCOUTE SUR `window`, PAS SUR LE PANNEAU : `ResumePersonne` n'expose
   * aucun point d'entrée pour un raccourci propre à l'organigramme, et il
   * n'a pas à en connaître l'existence — c'est un comportement de L'ÉCRAN,
   * pas du panneau. Depuis que le panneau est en lecture seule (06/10/2026),
   * parcourir la fratrie ne risque plus d'abandonner une saisie en cours. Neutralisée à l'intérieur d'un champ de saisie, sinon la
   * flèche y perdrait son sens habituel (déplacer le curseur, parcourir un
   * `<select>`).
   */
  useEffect(() => {
    if (!dossier) return undefined;
    const gerer = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const cible = e.target as HTMLElement | null;
      if (cible && ['INPUT', 'TEXTAREA', 'SELECT'].includes(cible.tagName)) return;
      const courant = affiches.find((u) => u.id === dossier);
      if (!courant) return;
      const fratrie = affiches.filter((u) => (u.responsable || null) === (courant.responsable || null));
      const idx = fratrie.findIndex((u) => u.id === dossier);
      if (idx === -1 || fratrie.length < 2) return;
      const suivant = e.key === 'ArrowDown'
        ? fratrie[(idx + 1) % fratrie.length]
        : fratrie[(idx - 1 + fratrie.length) % fratrie.length];
      e.preventDefault();
      ouvrirDossier(suivant.id);
    };
    window.addEventListener('keydown', gerer);
    return () => window.removeEventListener('keydown', gerer);
  }, [dossier, affiches]);

  // Insensible aux accents : « bguir » doit trouver « Bguir », et « pean »
  // « Péan ». Sans cela la recherche échoue sur la moitié des noms du labo.
  const norm = (s: unknown) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const qn = norm(q).trim();
  const trouve = new Set(
    qn ? affiches.filter((u) => norm(`${u.nom} ${u.fonction} ${u.id}`).includes(qn)).map((u) => u.id) : [],
  );

  /* La couleur choisie, indexée une fois pour tout l'arbre — la carte ne va pas
     la chercher elle-même. */
  /* Les fiches de fonction du dépôt, pour le sélecteur de la case. Chargées
     une fois à l'entrée en édition : hors édition, personne ne les regarde. */
  const [fichesDispo, setFichesDispo] = useState<{ code: string; libelle: string; reference: string }[]>([]);
  React.useEffect(() => {
    if (!edit) return;
    void fetch('/api/ged/ged/fonctions', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.ok) return;
        setFichesDispo((d.fonctions as { code: string; libelle: string; reference?: string }[])
          .map((x) => ({ code: x.code, libelle: x.libelle, reference: x.reference || '' })));
      })
      .catch(() => { /* le sélecteur retombe sur la valeur en place */ });
  }, [edit]);

  const couleurPersonne = React.useMemo(() => {
    const par = new Map(rhPersonnel.map((p) => [String(p.orgId), p.couleur || null]));
    return (orgId: string) => par.get(orgId) || null;
  }, [rhPersonnel]);

  const ctxArbre: CtxArbre = {
    edit, isRQ, moi: session.key, affiches, arbre, parId, porte, survole,
    setPorte, setSurvole, poser: (v) => setSurvole(() => v),
    /* La pastille du compte ouvre le MÊME résumé : il dit « Compte actif » dès
       son en-tête, c'est-à-dire exactement ce que la pastille fait demander. */
    rattacher, onOuvrir: (id) => ouvrirDossier(id), onOuvrirCompte: (id) => ouvrirDossier(id), trouve, sens, replies, basculerRepli,
    couleurPersonne,
  };

  /* ---- Organigramme fonctionnel (BIO24-A-ORG-01) -------------------------- */
  // Même principe que pour le nominatif : en édition on rend le BROUILLON,
  // pour que chaque frappe se voie immédiatement dans l'arbre.
  const fonctionsAffichees = edit && vue === 'fonctionnel' ? draftF : fonctions;
  const arbreF = fonctionsAffichees.map((f) => ({ id: f.id, parent: f.parent, lien: f.lien }));
  const fonctionParId = new Map(fonctionsAffichees.map((f) => [f.id, f]));
  /** Qui tient cette case, aujourd'hui. C'est la lecture croisée que l'audit
   *  demande : une case sans titulaire est un poste à pourvoir — sauf si elle
   *  est explicitement externalisée. */
  const titulaires = (fid: string) => users.filter((u) => (u.fonctionIds || []).includes(fid));

  /** Le CONTENU de la case fonctionnelle — extrait pour la même raison que
   *  `ContenuCarte` : le tracé serré (Lot 2, 03/09/2026) et le tracé classique
   *  doivent peindre exactement la même chose, sans le dupliquer. Le tracé
   *  serré passe cette carte par `renderToStaticMarkup` : aucun gestionnaire
   *  n'y survit, donc rien ici n'en porte — le clic est posé par l'appelant. */
  const ContenuCaseF = ({ f, tenue }: { f: OrgFonction; tenue: GedUser[] }) => (
    <>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{f.label}</div>
      {f.note && <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 4, lineHeight: 1.45 }}>{f.note}</div>}
      <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--text-muted)' }}>
        {f.externalise
          ? <span style={{ fontStyle: 'italic' }}>Prestataire externe</span>
          : tenue.length
            ? <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Fiche de fonction</span>
            : <span style={{ color: 'var(--amber-600)', fontWeight: 700 }}>Poste non pourvu</span>}
      </div>
    </>
  );

  const BrancheF = ({ id, profondeur = 0 }: { id: string; profondeur?: number }) => {
    const f = fonctionParId.get(id);
    if (!f || profondeur > 12) return null;
    const enfants = enfantsDe(arbreF, id);
    const tenue = titulaires(id);
    const couleur = couleurTier(f.tier);
    const h = sens === 'horizontal';
    const replie = replies.has(id);
    return (
      <div style={{ display: 'flex', flexDirection: h ? 'row' : 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {/* SANS noms de personnes (RQ, 19/08/2026) : le fonctionnel décrit
              des FONCTIONS — qui les tient se lit sur le nominatif. Rester
              « non pourvu » est en revanche une information de la fonction
              elle-même : elle demeure.
              La case OUVRE sa fiche de fonction quand elle en a une : c'est le
              document qui décrit la fonction, l'organigramme n'en est que la
              carte. */}
          {/* ⚠️ TOUJOURS LE PANNEAU, PLUS JAMAIS `openDoc` (RQ, 01/09/2026 :
              « le clic sur la fiche de fonction ne mène nulle part »).
              La case appelait `openDoc(f.docCode)` dès qu'elle portait un code.
              Or `docCode` est typé « code du document GED » mais porte en
              réalité la ligne `org-fonction` (`FN-TECHNICIEN`), et AUCUN
              document ne porte ces codes — 0 sur 80. Le clic quittait donc
              l'organigramme pour une fiche vide, ce qui est pire qu'un clic
              mort : on a perdu sa place ET on n'a rien lu.
              Le panneau, lui, rend maintenant la fiche entière — ce qui se
              déduit et ce qui a été rédigé — et dit ce que le document associé
              est, ou n'est pas. */}
          <div
            role="button"
            tabIndex={0}
            title={`Ouvrir la fiche de fonction — ${f.label}`}
            onClick={() => setFicheCase(f.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFicheCase(f.id); } }}
            style={{ ...card, padding: '12px 18px', minWidth: 190, maxWidth: 240, textAlign: 'center', borderTop: `3px solid ${couleur}`, ...(f.externalise ? { borderStyle: 'dashed' } : {}), cursor: 'pointer' }}>
            <ContenuCaseF f={f} tenue={tenue} />
          </div>
          {enfants.length > 0 && (
            <Replier n={enfants.length} replie={replie} onBascule={() => basculerRepli(id)} />
          )}
        </div>
        {enfants.length > 0 && !replie && (
          <>
            <Tige lien="hierarchique" sens={sens} />
            <div style={{
              display: 'flex', gap: h ? 6 : 8,
              flexDirection: h ? 'column' : 'row',
              alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'nowrap',
            }}>
              {enfants.map((e, i) => (
                <div key={e.id} style={{ display: 'flex', flexDirection: h ? 'row' : 'column', alignItems: 'center' }}>
                  <Connecteur lien={fonctionParId.get(e.id)?.lien || 'hierarchique'}
                    premier={i === 0} dernier={i === enfants.length - 1} sens={sens} />
                  <BrancheF id={e.id} profondeur={profondeur + 1} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const Legende = () => (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden style={{ width: 22, borderTop: '2px solid var(--border-strong)' }} />Lien hiérarchique
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden style={{ width: 22, borderTop: '2px dashed var(--text-faint)' }} />Rattachement fonctionnel (indépendance de la RQ)
      </span>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 22 }} className="organigramme-ecran">
      {/* Impression : A4 PAYSAGE, une page (RQ, 19/08). L'échelle écran (zoom)
          ne s'applique pas au papier ; on laisse le navigateur réduire par
          `zoom` CSS sur le conteneur imprimé — le contenu reste du texte, donc
          lisible, là où une capture réduite deviendrait floue. */}
      <style>{`@media print {
        @page { size: A4 landscape; margin: 8mm; }
        body * { visibility: hidden; }
        .organigramme-ecran, .organigramme-ecran * { visibility: visible; }
        .organigramme-ecran { position: absolute; left: 0; top: 0; width: 100%; }
        .organigramme-ecran [data-arbre] { zoom: 0.62; overflow: visible !important; }
        .ged-btn, select, input, [data-pastille-compte] { display: none !important; }
      }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <H1 eyebrow="Organisation" title="Organigramme" />
        {isRQ && !edit && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {vue === 'nominatif' && (
              <Bouton variante="primaire" taille="sm" onClick={() => setArrivee(true)}>
                <I n="plus" s={13} />Arrivée d’une personne
              </Bouton>
            )}
            <Bouton taille="sm" onClick={startEdit}>
              <I n="pen" s={13} />Modifier {vue === 'fonctionnel' ? 'les fonctions' : 'l’organigramme'}
            </Bouton>
          </div>
        )}
        {isRQ && edit && (
          <PiedActions taille="sm" annuler={{ onClick: annuler }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: saving, disabled: saving, onClick: saveOrg }} />
        )}
      </div>

      {/* Les deux documents du laboratoire, côte à côte. La bascule est
          désactivée en édition : on ne modifie que le nominatif ici, et changer
          de vue en cours de remaniement donnerait l'illusion d'avoir perdu le
          brouillon. */}
      <div role="tablist" aria-label="Type d’organigramme" style={{ display: 'flex', gap: 8 }}>
        <Segment ariaLabel="Vue" valeur={vue} onChange={setVue} options={([
          // Références SAISIES par le laboratoire (Paramétrage → Codification) :
          // ce sont celles de ses documents papier, elles étaient en dur ici.
          { k: 'nominatif' as const, label: 'Nominatif', ref: orgRefs.nominatif },
          { k: 'fonctionnel' as const, label: 'Fonctionnel', ref: orgRefs.fonctionnel },
          { k: 'fonctions' as const, label: 'Fiches de fonction', ref: '' },
        ]).map((t) => ({ valeur: t.k, libelle: <>{t.label}<span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: 0.75, marginInlineStart: 6 }}>{t.ref}</span></>, disabled: edit }))} />
      </div>

      {/* ⚠️ UNE ARRIVÉE VA AU DOSSIER, PAS AU RÉSUMÉ (06/10/2026) : la fiche
          qu'on vient de créer est presque vide — il faut la REMPLIR, et c'est au
          dossier RH que cela se fait. L'envoyer au résumé aurait affiché une
          colonne de tirets sans dire où saisir. */}
      {arrivee && (
        <FormulaireArrivee onFermer={() => setArrivee(false)}
          onCree={(id) => naviguerVers('dossiers', { cle: 'selectedPersonne', valeur: id })} />
      )}

      {dossier && (
        /* ── UN RÉSUMÉ, PLUS LA FICHE (RQ, 06/10/2026) ───────────────────────
           « L'organigramme donne juste une sorte de résumé non modifiable. »
           C'était `DossierPersonnel`, la fiche complète et éditable : on
           modifiait le dossier depuis l'écran de la STRUCTURE, et il y avait
           deux portes vers la même écriture. L'écriture vit désormais au seul
           dossier RH, où le résumé renvoie par un bouton.

           ⚠️ PLUS DE `resyncDraftDepuisMagasin` SUR CE CHEMIN : il resynchronisait
           le brouillon d'édition de l'arbre après un enregistrement de la fiche.
           Le résumé n'écrit rien — il n'y a plus rien à resynchroniser, et
           l'appeler laisserait croire le contraire à la relecture. La fonction
           reste utilisée par l'édition de l'arbre elle-même.

           `key` conservé : les flèches haut/bas passent d'une personne à
           l'autre sans démonter le panneau, et l'état interne (rien pour
           l'instant) ne doit pas suivre la personne précédente. */
        <ResumePersonne key={dossier} orgId={dossier} onFermer={() => setDossier(null)}
          // Le transfert se remplit au fil des choix : son panneau s'affiche
          // dans l'écran, jamais empilé dans un tiroir déjà défilant.
          onTransfert={(id) => { setDossier(null); setTransfert(id); }} />
      )}

      {transfert && <TransfertPanel sortantId={transfert} onClose={() => setTransfert(null)} />}
      {ficheCase && <PanneauFicheFonction id={ficheCase} onFermer={() => setFicheCase(null)} />}

      {/* ── LA CONFIRMATION DU RE-RATTACHEMENT (Lot 4) ─────────────────────────
          « Décrivant le changement en toutes lettres » (spec) : les deux noms
          sont dans le titre, pas seulement dans une case à cocher qu'on ne lit
          plus au bout du dixième geste. */}
      {confirmRatt && (
        <Modale
          titre="Confirmer le rattachement"
          sousTitre={`Rattacher ${confirmRatt.nomQui} à ${confirmRatt.nomVers} ?`}
          onClose={() => { if (!envoiRatt) { setConfirmRatt(null); setMotifRatt(''); } }}
          pied={<>
            <Bouton taille="sm" disabled={envoiRatt} onClick={() => { setConfirmRatt(null); setMotifRatt(''); }}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" disabled={envoiRatt || !motifRatt.trim()} onClick={() => void confirmerRattachement()}>
              {envoiRatt ? 'Enregistrement…' : 'Confirmer'}
            </Bouton>
          </>}
        >
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Cette modification porte sur <strong>BIO24-A-ORG-02</strong>, un document du système qualité.
            Elle ouvre (ou poursuit) une révision, et sera tracée au journal avec le motif ci-dessous.
          </p>
          <label htmlFor="motif-rattachement" style={etiquetteChamp}>Motif — obligatoire</label>
          <textarea id="motif-rattachement" autoFocus rows={3} value={motifRatt}
            onChange={(e) => setMotifRatt(e.target.value)}
            placeholder="Ce qui justifie ce changement de rattachement"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-sans)', width: '100%' }} />
        </Modale>
      )}

      {/* Ce que lit le personnel pendant qu'une révision se prépare — dit en
          clair : sans cette ligne, rien ne distingue la version qui s'applique. */}
      {(vue === 'nominatif' || vueF) && sourceOrg === 'approuvee' && (
        <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-body)' }}>
          {vueF ? 'BIO24-A-ORG-01' : 'BIO24-A-ORG-02'} — version {copieVue?.version} approuvée
          {copieVue?.dateApplication ? `, applicable le ${copieVue.dateApplication}` : ''}
          {' '}· une nouvelle version est en préparation.
        </div>
      )}
      {(vue === 'nominatif' || vueF) && sourceOrg === 'sansCopie' && (
        <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--amber-600)', background: 'var(--amber-50)' }}>
          Version en préparation, pas encore approuvée — aucune version approuvée n’est encore enregistrée dans
          l’application. Ce que vous voyez peut encore changer.
        </div>
      )}
      {(vue === 'nominatif' || vueF) && sourceOrg !== 'approuvee' && sourceOrg !== 'sansCopie' && (
        <CircuitOrganigramme
          key={vue}
          vue={vueF ? 'fonctionnel' : 'nominatif'}
          circuit={vueF ? circuitFonctionnel : circuitNominatif}
          isRQ={isRQ}
          estRH={session.roles.includes('rh')}
          moi={session.name}
          // Relire le paramétrage : sans cela le bandeau gardait l'état d'avant
          // le geste (« Vérifier » encore offert après une vérification réussie).
          onFait={(msg) => { showToast(msg); void refresh(); }}
          onErreur={(msg) => showToast(msg, 'erreur')}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {/* La RECHERCHE occupe ici la place qu'occupait la légende (retour Ines
            du 17/08). Elle prenait une ligne entière à elle seule, au-dessus de
            l'arbre, alors qu'elle sert en permanence ; la légende, elle, se
            consulte une fois et a rejoint le bas de page.
            Elle SURLIGNE et recentre, elle ne filtre pas : un arbre amputé de
            ses branches intermédiaires ne se lit plus. Réservée au nominatif —
            le fonctionnel décrit des cases, pas des personnes. */}
        {vue === 'nominatif' && !edit && affiches.length > 8 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: '1 1 260px', maxWidth: 460 }}>
            <ChampRecherche valeur={q} onChange={setQ} placeholder="Trouver une personne, une fonction…" style={{ ...inputStyle, height: 36, flex: '1 1 200px', maxWidth: 320 }} />
            {qn && (
              <>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: trouve.size ? 'var(--text-muted)' : 'var(--amber-600)' }}>
                  {trouve.size} sur {affiches.length}
                </span>
                <Bouton taille="sm" onClick={() => setQ('')}>Effacer</Bouton>
              </>
            )}
          </div>
        ) : <span style={{ flex: 1 }} />}
        <span style={{ flex: 1 }} />
        {/* Affichage : l'arbre dépasse le cadre dès une quinzaine de cases. Sans
            ces commandes il ne se lisait qu'au défilement, c'est-à-dire jamais
            en entier — or c'est la vue d'ensemble qu'on demande à un
            organigramme. */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Sens</span>
          <Segment ariaLabel="Sens" valeur={sens} onChange={setSens} options={([
            ['horizontal', 'En colonnes', 'La profondeur va vers la droite, l’effectif vers le bas — la page défile en hauteur, jamais en largeur.'],
            ['vertical', 'En arbre', 'Disposition classique : le responsable au-dessus, son équipe côte à côte. Large dès qu’une équipe compte plus de cinq personnes.'],
          ] as const).map(([k, label, aide]) => ({ valeur: k, libelle: label, aide: aide }))} />
          {trace === 'classique' && replies.size > 0 && (
            <Bouton taille="sm" onClick={() => setReplies(new Set())} title="Rouvrir toutes les branches repliées">Tout déplier ({replies.size})</Bouton>
          )}
          {trace === 'serre' && (
            <>
              <Bouton taille="sm" onClick={() => poignees.current?.toutDeplier()} title="Rouvrir toutes les branches">Tout déplier</Bouton>
              <Bouton taille="sm" onClick={() => poignees.current?.toutReplier()} title="Ne garder que les racines">Tout replier</Bouton>
            </>
          )}
          {/* ⚠️ LA BASCULE DE TRACÉ EST DITE, pas cachée : les deux ne rendent
              pas le même document. Le serré condense, le classique est celui
              des impressions déjà versées au dossier. */}
          <span style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '0 4px' }} />
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Tracé</span>
          <Segment ariaLabel="Tracé" valeur={trace} onChange={choisirTrace} options={([
            ['serre', 'Serré', 'Les cartes de hauteurs inégales se resserrent — la direction ne réserve plus sa hauteur à toute la rangée.'],
            ['classique', 'Classique', 'Le tracé d’origine, celui des impressions déjà versées au dossier d’audit.'],
          ] as const).map(([k, label, aide]) => ({ valeur: k, libelle: label, aide: aide }))} />
          <span style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '0 4px' }} />
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Taille</span>
          <Bouton variante={trace === 'serre' || zoom === null ? 'primaire' : 'contour'} taille="sm" onClick={() => { if (trace === 'serre') poignees.current?.ajuster(); else setZoom(null); }} aria-pressed={trace === 'classique' && zoom === null} title="Réduire l’arbre jusqu’à ce qu’il tienne dans la largeur">Ajuster</Bouton>
          <Bouton taille="sm" onClick={() => setZoom(Math.max(0.4, Math.round((echelle - 0.1) * 10) / 10))} disabled={echelle <= 0.4} aria-label="Réduire" title="Réduire">−</Bouton>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 42, textAlign: 'center' }}>
            {Math.round(echelle * 100)} %
          </span>
          <Bouton taille="sm" onClick={() => setZoom(Math.min(1.4, Math.round((echelle + 0.1) * 10) / 10))} disabled={echelle >= 1.4} aria-label="Agrandir" title="Agrandir">+</Bouton>
          {/* La VUE TABLEAU (Lot 5 §5.5) : l'ancien formulaire, conservé pour
              les tâches de masse, mais plus jamais affiché par défaut — cliquer
              une carte ouvre désormais le panneau d'édition directe. */}
          {edit && vue === 'nominatif' && (
            <>
              <span style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '0 4px' }} />
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>Vue</span>
              <Segment ariaLabel="Vue" valeur={vueEdition} onChange={setVueEdition} options={([
                ['arbre', 'Arbre', 'Cliquez une carte pour ouvrir son panneau d’édition — le mode par défaut.'],
                ['tableau', 'Vue tableau', 'L’ancien formulaire : une ligne par personne, pour vérifier d’un coup d’œil vingt-huit initiales ou repérer un doublon.'],
              ] as const).map(([k, label, aide]) => ({ valeur: k, libelle: label, aide: aide }))} />
            </>
          )}
        </div>
      </div>

      {vue === 'fonctions' && <FichesDeFonction estRQ={isRQ} estRH={session.roles.includes('rh')} />}

      {vue === 'fonctionnel' && (
        fonctionsAffichees.length === 0
          ? (
            <div style={{ ...card, padding: '14px 16px', borderInlineStart: '3px solid var(--amber-500)', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Aucune fonction n’est définie. {isRQ && (edit
                ? 'Ajoutez-en une depuis le tableau ci-dessous, ou repartez du document de référence.'
                : 'Utilisez « Modifier les fonctions » pour en créer, ou « Repartir de la trame d’origine ».')}
            </div>
          )
          : trace === 'serre' ? (
            /* ── LE MÊME TRACÉ SERRÉ, sur les CASES (Lot 2, 03/09/2026) ──────
               Même composant que le nominatif (`ArbreOrganigramme`), autre
               source (`fonctionsAffichees`) et autre gabarit (`ContenuCaseF`).
               Les deux alertes que l'audit exige survivent telles quelles :
               « Poste non pourvu » et le style pointillé des fonctions
               externalisées sont dans `ContenuCaseF`, donc dans les deux
               tracés à la fois. */
            <div data-arbre style={{ overflow: 'auto', paddingBottom: 8 }}>
              <ArbreOrganigramme
                donnees={fonctionsAffichees.map((f) => ({ id: f.id, parentId: f.parent, f }))}
                carte={(n) => (
                  <div style={{
                    ...card, padding: '12px 18px', height: '100%', boxSizing: 'border-box',
                    textAlign: 'center', borderTop: `3px solid ${couleurTier(n.f.tier)}`,
                    ...(n.f.externalise ? { borderStyle: 'dashed' } : {}),
                  }}>
                    <ContenuCaseF f={n.f} tenue={titulaires(n.f.id)} />
                  </div>
                )}
                hauteur={(n) => {
                  const lignesLabel = Math.ceil((n.f.label || '').length / 22);
                  const lignesNote = n.f.note ? Math.ceil(n.f.note.length / 30) : 0;
                  return 50 + lignesLabel * 16 + lignesNote * 16 + 20;
                }}
                largeur={() => 190}
                sens={sens === 'horizontal' ? 'left' : 'top'}
                compact
                onCarteClic={(id) => setFicheCase(id)}
                onPoignees={(p2) => { poignees.current = p2; }}
                hauteurVue={sens === 'horizontal' ? 700 : 620}
                nomEntite={settings?.branding?.labName || 'Laboratoire'}
              />
            </div>
          ) : (
            <div ref={cadre} data-arbre style={{
              // En colonnes l'arbre pousse en HAUTEUR (1 300 px ici) : borné et
              // défilant dans son propre cadre, il garde la barre d'outils et les
              // onglets sous les yeux, au lieu d'étirer la page sur trois écrans.
              overflow: 'auto', paddingBottom: 8,
              maxHeight: sens === 'horizontal' ? '68vh' : undefined,
            }}>
              {/* `width: max-content` + marges auto, et NON `justifyContent:
                  center` : un contenu centré plus large que son cadre déborde
                  des DEUX côtés, et le débordement de gauche n'est atteignable
                  par aucun défilement — la racine de l'arbre devenait
                  inaccessible. Ici l'arbre est centré tant qu'il tient, et
                  défile depuis la gauche dès qu'il dépasse.
                  `transformOrigin: top left` : centrer l'origine ferait fuir
                  l'arbre hors du cadre à mesure qu'il rétrécit. */}
              <div ref={interieur} style={{ display: 'flex', flexDirection: sens === 'horizontal' ? 'column' : 'row', gap: 10, alignItems: 'flex-start', width: 'max-content', margin: sens === 'horizontal' ? 0 : '0 auto', zoom: echelle }}>
                {racinesDe(arbreF).map((r) => <BrancheF key={r.id} id={r.id} />)}
              </div>
            </div>
          )
      )}

      {/* Repartir du document de référence. Sans cette commande, une remise à
          plat était impossible : le serveur n'écrit les cases qu'au tout premier
          démarrage et s'abstient ensuite (garde `orgVersion`) — redémarrer ne
          restaurait donc rien. */}
      {isRQ && vue === 'fonctionnel' && !edit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <ConfirmButton
            label="Repartir de la trame d’origine"
            variant="outline"
            icon="refresh"
            question={`Remplacer les ${fonctionsAffichees.length} case(s) actuelles par la trame d’origine (organigramme V02 intégré le 18/05/2026) ? Les personnes ne sont pas touchées, mais celles qui tenaient une case disparue en seront détachées.`}
            confirmer="Réinitialiser"
            onConfirm={reinitialiserFonctions}
          />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Efface les cases actuelles et réécrit la trame d’origine — celle chargée à la mise en
            service (organigramme V02 du 18/05/2026). Ce n’est PAS un document de la GED : quand
            l’organigramme fonctionnel y sera géré comme document, cette commande repartira de sa
            version en vigueur.
          </span>
        </div>
      )}

      {/* En édition, la Vue tableau REMPLACE l'arbre — deux éditeurs simultanés
          du même brouillon auraient vite divergé sous les yeux. */}
      {vue === 'nominatif' && (!edit || vueEdition === 'arbre') && (
        <>
          {edit && (
            // Zone de dépôt « racine » : sans elle, une personne rattachée par
            // erreur ne peut plus être détachée à la souris — il n'existe aucune
            // carte représentant « personne ».
            <div
              onDragOver={(e) => { if (porte) { e.preventDefault(); setSurvole('__racine__'); } }}
              onDragLeave={() => setSurvole((s) => (s === '__racine__' ? null : s))}
              onDrop={(e) => { e.preventDefault(); if (porte) rattacher(porte, null); setPorte(null); setSurvole(null); }}
              style={{
                padding: '10px 14px', textAlign: 'center', fontSize: 12.5,
                color: 'var(--text-muted)', borderRadius: 'var(--radius-md)',
                border: `2px dashed ${survole === '__racine__' ? 'var(--color-primary)' : 'var(--border-strong)'}`,
                background: survole === '__racine__' ? 'var(--surface-sunken)' : 'transparent',
              }}
            >
              Glissez une vignette sur une autre pour la rattacher — ou ici pour la remettre à la racine.
            </div>
          )}
          {/* ── LE TRACÉ SERRÉ (Lot 1, 03/09/2026) ─────────────────────────
              `d3-flextree`, embarqué par `d3-org-chart`, produit un arbre serré
              MÊME quand les cartes ont des hauteurs très différentes. C'est
              exactement le défaut constaté : la carte du Dr Hmidet fait quatre
              fois celle d'un « Préleveur », et le tracé en `flex` réservait cette
              hauteur à toute la rangée.

              ⚠️ LE TRACÉ CLASSIQUE RESTE, derrière une bascule. Ce n'est pas de
              l'indécision : cet écran EST le document BIO24-A-ORG-02, il
              s'imprime et se présente en audit. Remplacer son moteur de tracé
              sans filet, sur une pièce accréditée, est le genre de geste dont on
              ne mesure le coût qu'au moment où il faut revenir en arrière.
              La bascule est faite pour être RETIRÉE une fois le tracé serré
              éprouvé sur plusieurs impressions réelles. */}
          {trace === 'serre' ? (
            <div data-arbre style={{ overflow: 'auto', paddingBottom: 8 }}>
              <ArbreOrganigramme
                donnees={affiches.map((u) => ({ id: u.id, parentId: u.responsable ?? null, u }))}
                /* Le MÊME gabarit que le tracé classique — `ContenuCarte`. */
                carte={(n) => (
                  <div style={{
                    ...card, padding: '7px 10px', height: '100%', boxSizing: 'border-box',
                    textAlign: 'center', borderTop: `3px solid ${couleurTier(tierOfUser(n.u))}`,
                    position: 'relative',
                  }}>
                    <PastilleCompte u={n.u} />
                    <ContenuCarte u={n.u} couleurPersonne={couleurPersonne} />
                  </div>
                )}
                /* ⚠️ LA HAUTEUR EST ESTIMÉE, PAS MESURÉE : `d3-org-chart` l'exige
                   AVANT de rendre. Trop basse, les cartes se chevauchent ; trop
                   haute, on retrouve le gaspillage qu'on vient de supprimer. On
                   la calcule donc depuis ce que la carte porte réellement —
                   l'intitulé et sa longueur, la pastille d'indisponibilité. */
                hauteur={(n) => {
                  const lignesIntitule = Math.ceil((n.u.fonction || '').length / 22);
                  return 62 + lignesIntitule * 16 + (estAffectable(n.u) ? 0 : 22)
                    + ((n.u.roles || []).length ? 14 : 0);
                }}
                largeur={() => 170}
                sens={sens === 'horizontal' ? 'left' : 'top'}
                compact
                surlignes={[...trouve]}
                /* Le tracé serré n'a pas de glisser-déposer (Lot 4 limité au
                   tracé classique) : le clic ouvre donc la fiche dans les
                   deux modes, exactement comme le tracé classique (Lot 5). */
                onCarteClic={(id) => ouvrirDossier(id)}
                onPoignees={(p2) => { poignees.current = p2; }}
                hauteurVue={sens === 'horizontal' ? 700 : 620}
                /* Le nom RÉEL du laboratoire, pas une étiquette générique : la
                   racine s'imprime sur le document d'organisation. */
                nomEntite={settings?.branding?.labName || 'Laboratoire'}
              />
            </div>
          ) : (
          <div ref={cadre} data-arbre style={{
              // En colonnes l'arbre pousse en HAUTEUR (1 300 px ici) : borné et
              // défilant dans son propre cadre, il garde la barre d'outils et les
              // onglets sous les yeux, au lieu d'étirer la page sur trois écrans.
              overflow: 'auto', paddingBottom: 8,
              maxHeight: sens === 'horizontal' ? '68vh' : undefined,
            }}>
            <div ref={interieur} style={{ display: 'flex', flexDirection: sens === 'horizontal' ? 'column' : 'row', gap: 10, alignItems: 'flex-start', width: 'max-content', margin: sens === 'horizontal' ? 0 : '0 auto', zoom: echelle }}>
              {racinesDe(arbre).map((r) => <Branche key={r.id} id={r.id} ctx={ctxArbre} />)}
            </div>
          </div>
          )}
        </>
      )}

      {/* La légende se lit UNE fois, quand on découvre l'arbre : sa place est
          SOUS lui, pas au-dessus, où elle repoussait l'organigramme hors de
          l'écran à chaque ouverture (retour Ines du 17/08) — et la barre de
          recherche a pris la place qu'elle occupait dans la barre d'outils.
          Posée ici, après les deux vues : nominative et fonctionnelle emploient
          les mêmes traits, une seule légende les explique. */}
      {!edit && <Legende />}

      {edit && vue === 'fonctionnel' && (
        <div style={{ ...card, padding: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 4 }}>Fonctions du laboratoire</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Les CASES de l’organisation, indépendantes des personnes qui les tiennent.
            Le <strong>rattachement</strong> décide du trait qui relie la case à sa mère :
            plein s’il est hiérarchique, <strong>pointillé s’il est fonctionnel</strong> — c’est
            ce second trait qui matérialise une unité indépendante, le responsable qualité au premier chef.
          </p>
          {/* Une FICHE par case, et non une ligne de sept colonnes serrées :
              à cette largeur, les libellés se coupaient en plein milieu
              (« n du laboratoire », « Biologist ») et les cinq listes se
              suivaient SANS en-tête — rien ne disait laquelle réglait quoi.
              Chaque champ porte désormais son nom, et la grille se replie
              d'elle-même quand la place manque. */}
          <div style={{ display: 'grid', gap: 10 }}>
            {draftF.map((f, i) => (
              <div key={f.id} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <label style={etiquetteChamp} htmlFor={`fn-lib-${f.id}`}>Libellé de la case</label>
                    <input id={`fn-lib-${f.id}`} autoFocus={i === 0} value={f.label} placeholder="Ex. Réf. Hématologie / Coagulation"
                      onChange={(e) => patchFonction(f.id, { label: e.target.value })} style={inputStyle} />
                  </div>
                  <label title="Case tenue par un prestataire extérieur : elle n’est pas comptée comme poste à pourvoir."
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-body)', cursor: 'pointer', height: 40 }}>
                    <input type="checkbox" checked={!!f.externalise}
                      onChange={(e) => patchFonction(f.id, { externalise: e.target.checked })}
                      style={{ accentColor: 'var(--color-primary)', width: 15, height: 15 }} />
                    Prestataire externe
                  </label>
                  <Bouton onClick={() => removeFonction(f.id)} title="Retirer cette case du brouillon" aria-label={`Retirer ${f.label || 'cette case'}`}>
                    <I n="trash" s={14} />
                  </Bouton>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <label style={etiquetteChamp} htmlFor={`fn-tier-${f.id}`}>Niveau</label>
                    <Selecteur id={`fn-tier-${f.id}`} value={f.tier} onChange={(v) => patchFonction(f.id, { tier: v })} style={inputStyle}>
                      {ORG_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </Selecteur>
                  </div>
                  {/* Une case ne peut pas être sa propre mère, ni descendre sous
                      l'une de ses filles : la garde anti-cycle du nominatif vaut
                      ici mot pour mot — un arbre cyclique se parcourt à l'infini. */}
                  <div style={{ minWidth: 0 }}>
                    {/* ⚠️ CE SÉLECTEUR PROPOSAIT DES DOCUMENTS DE LA GED (02/09/2026).
                        C'était juste tant que la fiche de fonction devait devenir
                        un document — ce n'est plus le cas : depuis l'arbitrage du
                        01/09, la fiche vit dans `org-fonction` avec son propre
                        circuit léger, et `docCode` est devenu la CLÉ DE JOINTURE
                        entre la case et cette fiche.
                        Y choisir un document de la GED cassait donc le lien EN
                        SILENCE : la case ne trouvait plus ses rubriques rédigées,
                        et le panneau se remettait à annoncer « à rédiger » ce qui
                        était écrit. On propose les fiches, puisque c'est à elles
                        que le champ renvoie. */}
                    <label style={etiquetteChamp} htmlFor={`fn-doc-${f.id}`}>Fiche de fonction</label>
                    <Selecteur id={`fn-doc-${f.id}`} value={f.docCode || ''}
                      onChange={(v) => patchFonction(f.id, { docCode: v || null })} style={inputStyle}>
                      <option value="">— aucune</option>
                      {fichesDispo.map((x) => (
                        <option key={x.code} value={x.code}>
                          {x.code} — {x.libelle}{x.reference ? ` (${x.reference})` : ''}
                        </option>
                      ))}
                      {/* Une valeur héritée qui ne correspond à aucune fiche doit
                          rester visible : la masquer la ferait remplacer sans
                          qu'on sache qu'elle a existé. */}
                      {f.docCode && !fichesDispo.some((x) => x.code === f.docCode) && (
                        <option value={f.docCode}>{f.docCode} — ne correspond à aucune fiche</option>
                      )}
                    </Selecteur>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={etiquetteChamp} htmlFor={`fn-par-${f.id}`}>Rattachée à</label>
                    <Selecteur id={`fn-par-${f.id}`} value={f.parent || ''} aria-label={`Rattachement de ${f.label || 'la case'}`}
                      onChange={(v) => patchFonction(f.id, { parent: v || null })} style={inputStyle}>
                      <option value="">— Racine</option>
                      {draftF
                        .filter((o) => o.id !== f.id && !estDescendant(arbreF, f.id, o.id))
                        .map((o) => <option key={o.id} value={o.id}>{o.label || o.id}</option>)}
                    </Selecteur>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={etiquetteChamp} htmlFor={`fn-lien-${f.id}`}>Trait de rattachement</label>
                    <Selecteur id={`fn-lien-${f.id}`} value={f.lien} aria-label={`Type de lien de ${f.label || 'la case'}`}
                      onChange={(v) => patchFonction(f.id, { lien: v as OrgFonction['lien'] })} style={inputStyle}>
                      <option value="hierarchique">Trait plein (hiérarchique)</option>
                      <option value="fonctionnel">Pointillé (unité indépendante)</option>
                    </Selecteur>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={etiquetteChamp} htmlFor={`fn-note-${f.id}`}>Note</label>
                    <input id={`fn-note-${f.id}`} value={f.note || ''} placeholder="Facultative"
                      onChange={(e) => patchFonction(f.id, { note: e.target.value })} style={inputStyle} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Bouton taille="sm" onClick={addFonction} style={{ marginTop: 12 }}>
            <I n="plus" s={13} />Ajouter une fonction
          </Bouton>
          {/* ⚠️ Ce paragraphe annonçait « la fiche de la personne », où le champ
              est en LECTURE SEULE : il renvoyait vers un geste qui n'existait
              nulle part (GED-6). Il désigne désormais l'endroit réel. */}
          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '10px 0 0' }}>
            Qui tient chaque case se règle dans l’onglet <strong>Nominatif</strong>, en édition : chaque personne y
            porte ses <strong>postes tenus</strong>, et peut en tenir plusieurs. Pas ici : une case existe
            indépendamment de son titulaire, et c’est ce qui permet de dire « poste non pourvu ».
          </p>
        </div>
      )}

      {/* GED-8 — le rapprochement se lit dans l'organigramme NOMINATIF, hors
          édition : c'est un constat, pas une saisie. Réservé au RQ, qui seul
          peut en tirer une décision. */}
      {isRQ && vue === 'nominatif' && !edit && <RapprochementPanel />}

      {edit && vue === 'nominatif' && vueEdition === 'tableau' && (
        <div style={{ ...card, padding: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 10 }}>Identités</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {draft.map((u, i) => (
              <div key={u.id} style={{ display: 'grid', gap: 6, padding: fonctions.length ? '8px 0' : 0, borderBottom: fonctions.length && i < draft.length - 1 ? '1px solid var(--border-default)' : undefined }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 70px 150px 32px', gap: 8, alignItems: 'center' }}>
                  {/* Le bouton « Modifier l'organigramme » disparaît en entrant en
                      édition : sans ce focus, le clavier repartait du haut du
                      document au lieu d'entrer dans le formulaire. */}
                  {/* ⚠️ EN LECTURE DEPUIS LE 07/09/2026, comme la fonction juste à
                      côté : le serveur n'écrit plus `nom` depuis cette liste. Le
                      nom composé vient du dossier RH (titre, prénom, patronyme) et
                      c'est là qu'il se corrige — un champ ici aurait dit
                      « enregistré » sans rien enregistrer. */}
                  <span title="Le nom se corrige dans le dossier RH de la personne"
                    style={{ ...inputStyle, display: 'flex', alignItems: 'center', background: 'var(--gray-25)', color: 'var(--text-body)', cursor: 'default' }}>
                    {u.nom || '—'}
                  </span>
                  {/* ⚠️ EN LECTURE DEPUIS LE 02/09/2026. `synchroniserDepuisVue`
                      n'écrit plus `fonction` : laisser un champ de saisie ici
                      aurait produit le pire des retours — on tape, on
                      enregistre, l'écran répond « enregistré », et rien ne
                      change. C'est « champ écrit mais non relu », le défaut le
                      plus cher de ce projet.
                      L'intitulé se change en cochant un poste tenu (ligne du
                      dessous) ou en désignant un pilote sur la cartographie. */}
                  <div title="Intitulé calculé — il suit les postes tenus, les pilotages de processus et les rôles nominatifs."
                    style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: 'var(--text-muted)',
                      background: 'var(--gray-25)', cursor: 'help', overflow: 'hidden', whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis' }}>
                    {u.fonction || '—'}
                  </div>
                  {/* ⚠️ EN LECTURE DEPUIS LE 02/10/2026 : les initiales se saisissent à UN
                      seul endroit, la fiche de la personne (à côté du nom). Un second
                      champ ici permettait de les écrire sans le contrôle d'unicité de
                      la fiche. */}
                  <span title="Les initiales se saisissent sur la fiche de la personne"
                    style={{ ...inputStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-25)',
                      color: 'var(--text-body)', cursor: 'default', fontFamily: 'var(--font-mono)' }}>
                    {u.initiales || initials(u.nom)}
                  </span>
                  <select value={tierOfUser(u)} onChange={(e) => patchUser(u.id, { orgTier: e.target.value })} style={inputStyle}>
                    {ORG_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  {/* On ne retire librement qu'une ligne À PEINE ajoutée. Une
                      personne déjà enregistrée ne se supprime pas ici : on
                      l'archive depuis le module Site (son historique la référence). */}
                  {estCompte(u.id) ? (
                    <span title="Compte : pour le retirer, archivez-le depuis le module Site — son historique reste préservé." style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px 8px', color: 'var(--text-faint)', cursor: 'help' }}><I n="lock" s={13} /></span>
                  ) : (
                    <Bouton taille="sm" onClick={() => removeUser(u.id)} title="Retirer cette ligne" aria-label="Retirer cette ligne"><I n="trash" s={13} /></Bouton>
                  )}
                </div>
                <PostesTenus u={u} fonctions={fonctions} onChange={(ids) => patchUser(u.id, { fonctionIds: ids })} />
              </div>
            ))}
          </div>
          {/* ⚠️ « AJOUTER UNE PERSONNE » A ÉTÉ RETIRÉ D'ICI (RQ, 01/09/2026).
              « C'est une case dessinée, pas une arrivée. »

              Le bouton posait une ligne d'identifiant `u_<base36>` que
              l'enregistrement transformait en vraie fiche de personnel — sans
              matricule, sans badge, sans date d'entrée, sans compte et sans
              invitation. Pire, `u_ma3f2b` ne respecte pas la règle
              d'identifiant du module (`^[a-z0-9.]+$`, refusée par
              `creerPersonnel`) : il ne pourra JAMAIS correspondre à un
              `up_users.username`, donc jamais porter de compte, et il
              apparaîtra pour toujours au rapprochement comme « personne sans
              compte ». Deux portes d'entrée pour le même geste, dont une qui
              fabrique des dossiers incomplets.

              Le serveur refuse désormais ces identifiants à la création
              (`synchroniserDepuisVue`) : le chemin est fermé, pas seulement
              caché. Les lignes `u_…` déjà en base restent modifiables et
              retirables — on ne casse pas ce qui existe. */}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.55 }}>
            Une personne <strong>ne s’ajoute pas ici</strong> : cet écran remanie l’organisation de
            gens qui existent. Une arrivée se saisit avec le bouton <strong>« Arrivée d’une
            personne »</strong>, hors édition — c’est lui qui recueille le matricule, le badge, la
            date d’entrée et le compte de connexion, et qui envoie l’invitation.
          </p>
          {fonctions.length > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
              Une personne peut tenir <strong>plusieurs cases</strong> de l’organigramme fonctionnel — c’est le cas
              de la coordination et des référents. Chaque case cochée la fait apparaître comme titulaire dans
              l’onglet <strong>Fonctionnel</strong> ; une case que personne ne coche s’y lit « poste non pourvu ».
            </p>
          )}
          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '10px 0 0' }}>Les rôles documentaires (RQ, rédacteur…) se règlent dans le Paramétrage Qualité. Ici : identité, niveau hiérarchique et postes tenus. Une personne qui part se retire en <strong>archivant son compte</strong> (module Site), jamais en la supprimant : son historique la référence.</p>
        </div>
      )}
      {confirmation}
    </div>
  );
}

/* ─────────── Rapprochement comptes ↔ organigramme (lot GED-8) ───────────── */

const GRAVITE: Record<string, { bg: string; fg: string; label: string }> = {
  critique: { bg: 'var(--red-50)', fg: 'var(--red-600)', label: 'À traiter' },
  attention: { bg: 'var(--amber-50)', fg: 'var(--amber-600)', label: 'À confirmer' },
  information: { bg: 'var(--gray-50)', fg: 'var(--text-muted)', label: 'Pour information' },
};

/** Une pastille chiffrée de l'en-tête : le chiffre d'abord, le mot ensuite. */
function Chiffre({ n, label, alerte }: { n: number; label: string; alerte?: boolean }) {
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 'var(--radius-md)', minWidth: 92,
      background: alerte && n > 0 ? 'var(--red-50)' : 'var(--surface-sunken)',
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 19, fontWeight: 700,
        color: alerte && n > 0 ? 'var(--red-600)' : 'var(--text-strong)',
      }}>{n}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.35 }}>{label}</div>
    </div>
  );
}

/**
 * Comptes qui ne correspondent à personne, et personnes qui n'ont pas de compte.
 *
 * POURQUOI CET ÉCRAN. Aucun rapprochement n'existait entre les comptes de la
 * suite et l'organigramme. C'est une question d'audit autant que de sécurité :
 * un compte actif qui ne correspond à personne d'identifié est exactement ce
 * qu'un auditeur relève, et c'est par là qu'un accès oublié survit à un départ.
 * Le rapprochement se lit ici parce que c'est ici qu'on regarde « qui est chez
 * nous » — pas dans une console d'administration que le RQ n'ouvre jamais.
 *
 * ⚠️ CET ÉCRAN NE SUPPRIME NI NE DÉSACTIVE RIEN, et il n'existe aucune route
 * pour le faire depuis ce lot. La purge de comptes du 28/07/2026 s'est faite
 * PAR RÉATTRIBUTION, jamais par suppression, pour que les accusés de lecture et
 * le journal chaîné restent intacts. On signale, la décision reste humaine —
 * et elle se prend dans le module Site, où le cycle de vie d'un compte vit.
 *
 * Chargement À LA DEMANDE : la lecture parcourt tous les comptes et tout le
 * personnel, et cet écran est d'abord un organigramme. On affiche les chiffres
 * dès l'ouverture du panneau, jamais avant.
 */
function RapprochementPanel() {
  const { chargerRapprochementComptes } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [r, setR] = useState<RapprochementComptes | null>(null);
  const [charge, setCharge] = useState(false);

  const ouvrir = async () => {
    setOuvert((v) => !v);
    if (charge) return;
    setCharge(true);
    setR(await chargerRapprochementComptes());
  };

  const c = r?.chiffres;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
            Comptes de connexion et organigramme
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.5 }}>
            Qui a un compte sans figurer au personnel, et qui figure au personnel sans compte.
            Rien n’est modifié ici : le rapprochement se lit, la décision se prend.
          </p>
        </div>
        <Bouton variante={ouvert ? 'contour' : 'primaire'} taille="sm" onClick={() => void ouvrir()} aria-expanded={ouvert}>
          <I n={ouvert ? 'x' : 'search'} s={13} />{ouvert ? 'Masquer' : 'Rapprocher'}
        </Bouton>
      </div>

      {ouvert && !r && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '14px 0 0' }}>
          {charge ? 'Rapprochement en cours…' : 'Rapprochement indisponible.'}
        </p>
      )}

      {ouvert && r && c && (
        <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chiffre n={c.apparies} label="personnes avec compte" />
            <Chiffre n={c.comptesSansPersonne} label="comptes sans personne" alerte />
            <Chiffre n={c.personnesSansCompte} label="personnes sans compte" />
            <Chiffre n={c.liensIncoherents} label="fiches incohérentes" alerte />
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            {c.comptes} compte(s) dont {c.comptesActifs} actif(s) · {c.personnel} personne(s) au
            personnel dont {c.personnelDansOrganigramme} dans l’organigramme.
          </p>

          {/* ---- Sens 1 : comptes sans personne ---- */}
          <section>
            <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
              Comptes ne correspondant à personne · {c.comptesSansPersonne}
              {c.comptesSansPersonneCritiques > 0 && (
                <span style={{ color: 'var(--red-600)' }}> — dont {c.comptesSansPersonneCritiques} interne(s) actif(s)</span>
              )}
            </h4>
            {!r.comptesSansPersonne.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--green-600)', margin: 0 }}>
                Aucun : tout compte correspond à une personne connue du personnel.
              </p>
            ) : r.comptesSansPersonne.map((o) => {
              const g = GRAVITE[o.gravite] || GRAVITE.information;
              return (
                <div key={o.username} style={{ display: 'grid', gap: 3, padding: '8px 0', borderTop: '1px solid var(--border-default)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{o.username}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: g.bg, color: g.fg }}>{g.label}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      profil {o.profil} · {o.etat}
                      {o.email ? ` · ${o.email}` : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {o.motif}
                    {/* La dernière connexion est le fait qui tranche : un compte
                        orphelin qui s'est connecté cette semaine n'est pas un
                        reliquat administratif. */}
                    {o.derniereConnexion
                      ? <strong style={{ color: 'var(--red-600)' }}> Dernière connexion : {o.derniereConnexion}.</strong>
                      : <span> Jamais connecté{o.creeLe ? ` (créé le ${o.creeLe})` : ''}.</span>}
                  </div>
                </div>
              );
            })}
          </section>

          {/* ---- Sens 2 : personnes sans compte ---- */}
          <section>
            <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
              Personnes sans compte · {c.personnesSansCompte}
              {c.personnesSansCompteAProposer > 0 && (
                <span style={{ color: 'var(--amber-600)' }}> — dont {c.personnesSansCompteAProposer} dont la fonction en appellerait un</span>
              )}
            </h4>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
              Ce n’est pas une anomalie en soi : ici, la majorité du personnel n’a pas de compte.
              Mais sans compte, une personne <strong>ne reçoit aucune diffusion et n’accuse aucune lecture</strong>.
            </p>
            {!r.personnesSansCompte.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Aucune.</p>
            ) : r.personnesSansCompte.map((p) => (
              <div key={p.orgId} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border-default)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{nomDe(p)}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.fonction || '—'}</span>
                {p.statut !== 'actif' && (
                  <span style={{ fontSize: 10.5, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--gray-50)', color: 'var(--text-muted)' }}>{p.statut}</span>
                )}
                {!p.dansOrganigramme && (
                  <span style={{ fontSize: 10.5, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--gray-50)', color: 'var(--text-muted)' }}>hors organigramme</span>
                )}
                {p.profilPropose && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>
                    appellerait un profil {p.profilPropose}
                  </span>
                )}
              </div>
            ))}
          </section>

          {/* ---- Fiches qui ne disent pas la vérité ---- */}
          {r.liensIncoherents.length > 0 && (
            <section>
              <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Fiches et comptes qui se contredisent · {c.liensIncoherents}
              </h4>
              {r.liensIncoherents.map((l) => (
                <div key={l.orgId} style={{ padding: '6px 0', borderTop: '1px solid var(--border-default)' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{l.nom}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)', marginInlineStart: 8 }}>{l.motif}</span>
                </div>
              ))}
            </section>
          )}

          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0, lineHeight: 1.55 }}>
            Le cycle de vie d’un compte (suspendre, archiver) se règle dans le module Site, et le
            dossier d’une personne depuis sa fiche. <strong>Rien ne se supprime</strong> : un compte
            archivé garde ses accusés de lecture et sa place dans le journal chaîné (ISO 15189 §8.3).
          </p>
        </div>
      )}
    </div>
  );
}

/** Une catégorie d'engagement : compte, liste des références, et le sélecteur
 *  de successeur — masqué quand la catégorie est vide (rien à transférer). */
function Bloc({ titre, lignes, children }: { titre: string; lignes: EngagementLigne[]; children?: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: lignes.length ? 'var(--amber-600)' : 'var(--text-faint)' }}>
          {lignes.length}
        </span>
      </div>
      {lignes.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
          {lignes.map((l) => `${l.code}${l.statut ? ` (${l.statut})` : ''}`).join(' · ')}
        </div>
      )}
      {lignes.length > 0 && children && <div style={{ marginTop: 9 }}>{children}</div>}
    </div>
  );
}

/* ------------------- Transfert de responsabilités (RQ) ------------------- */
/**
 * Départ de l'établissement (compte archivé) ou absence longue (compte
 * suspendu) : on inventorie ce que la personne porte encore, puis on réaffecte
 * catégorie par catégorie — à une ou plusieurs personnes pour la rédaction.
 *
 * Le passé n'est jamais réécrit : les versions, signatures et journaux gardent
 * leur auteur. Seules les responsabilités À VENIR changent de main.
 */
function TransfertPanel({ sortantId, onClose }: { sortantId: string; onClose: () => void }) {
  const { settings, loadEngagements, transferer } = useGed();
  const [eng, setEng] = useState<Engagements | null>(null);
  const [busy, setBusy] = useState(false);
  const [p, setP] = useState<TransfertPayload>({ redaction: [], accuses: 'retirer', accusesVers: [] });

  useEffect(() => {
    let vivant = true;
    void loadEngagements(sortantId).then((e) => { if (vivant) setEng(e); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortantId]);

  const sortant = (settings?.gedUsers || []).find((u) => u.id === sortantId);
  // Successeurs possibles : tout le monde SAUF la personne sortante et les confrères.
  const candidats = personnelReel(settings?.gedUsers || []).filter((u) => u.id !== sortantId && estAffectable(u));
  const porteurs = (role: string) => candidats.filter((u) => (u.roles || []).includes(role));

  const appliquer = async () => {
    setBusy(true);
    const ok = await transferer(sortantId, p);
    setBusy(false);
    if (ok) { const frais = await loadEngagements(sortantId); setEng(frais); }
  };

  return (
    <div style={{ ...card, padding: 18, borderInlineStart: '3px solid var(--color-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-primary)' }}>Départ / absence longue</div>
          <h2 style={{ margin: '6px 0 0', fontSize: 17, fontWeight: 700, color: 'var(--text-strong)' }}>
            Transférer les responsabilités de {sortant?.nom || sortantId}
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '72ch' }}>
            Seules les responsabilités <strong>en cours</strong> changent de main. Les versions rédigées, les
            signatures scellées et le journal gardent son nom — la traçabilité ne se réécrit pas.
          </p>
        </div>
        <button onClick={onClose} aria-label="Fermer" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><I n="x" s={18} /></button>
      </div>

      {!eng ? (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 14 }}>Inventaire en cours…</p>
      ) : eng.total === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--green-600)', fontWeight: 600, marginTop: 14 }}>
          Aucune responsabilité en cours : le compte peut être archivé sans laisser de circuit orphelin.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          <Bloc titre="Documents où la personne est rédacteur affecté" lignes={eng.redaction}>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 5 }}>Repris par (plusieurs possible) :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {porteurs('redacteur').map((u) => (
                <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={(p.redaction || []).includes(u.id)}
                    onChange={(e) => setP({ ...p, redaction: e.target.checked ? [...(p.redaction || []), u.id] : (p.redaction || []).filter((k) => k !== u.id) })} />
                  {u.nom}
                </label>
              ))}
            </div>
          </Bloc>

          <Bloc titre="Documents à vérifier par la personne" lignes={eng.verification}>
            <Selecteur value={p.verification || ''} onChange={(v) => setP({ ...p, verification: v })} style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              <option value="">— choisir un vérificateur —</option>
              {porteurs('verificateur').map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </Bloc>

          <Bloc titre="Documents à approuver par la personne" lignes={eng.approbation}>
            <Selecteur value={p.approbation || ''} onChange={(v) => setP({ ...p, approbation: v })} style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              <option value="">— choisir un approbateur —</option>
              {porteurs('approbateur').map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </Bloc>

          <Bloc titre="Processus pilotés" lignes={eng.pilotage}>
            <Selecteur value={p.pilotage || ''} onChange={(v) => setP({ ...p, pilotage: v })} style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              <option value="">— choisir un pilote —</option>
              {candidats.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </Bloc>

          <Bloc titre="Non-conformités, actions et réclamations ouvertes" lignes={eng.smq}>
            <Selecteur value={p.smq || ''} onChange={(v) => setP({ ...p, smq: v })} style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              <option value="">— choisir un responsable —</option>
              {candidats.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </Bloc>

          <Bloc titre="Accusés de lecture encore dus" lignes={eng.accuses}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
              {([['retirer', 'Retirer l’exigence (personne sortie)'], ['transferer', 'Reporter sur d’autres lecteurs']] as const).map(([k, label]) => (
                <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="radio" name="accuses" checked={(p.accuses || 'retirer') === k} onChange={() => setP({ ...p, accuses: k })} />
                  {label}
                </label>
              ))}
            </div>
            {p.accuses === 'transferer' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {candidats.map((u) => (
                  <label key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                    <input type="checkbox" checked={(p.accusesVers || []).includes(u.id)}
                      onChange={(e) => setP({ ...p, accusesVers: e.target.checked ? [...(p.accusesVers || []), u.id] : (p.accusesVers || []).filter((k) => k !== u.id) })} />
                    {u.nom}
                  </label>
                ))}
              </div>
            )}
          </Bloc>

          {/* ⚠️ LECTURE SEULE, et c'est le seul bloc de cet écran qui l'est.
              Une habilitation est une attestation de compétence PASSÉE : elle ne
              se transfère pas (on ne donne pas la compétence de quelqu'un à
              quelqu'un d'autre) et elle ne s'effacerait pas sans réécrire le
              passé. Le départ doit seulement DIRE la couverture qu'il emporte,
              pour que la RQ décide au registre — d'où un bloc sans sélecteur de
              successeur. Avant le 12/10/2026, le départ n'en parlait pas du
              tout : deux partants détenaient encore neuf habilitations. */}
          <Bloc titre="Habilitations détenues — à reprendre au registre des compétences" lignes={eng.habilitations || []}>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Elles ne se transfèrent pas et ne sont pas effacées ici : elles attestent une compétence
              réelle à la date où la personne a signé. À <strong>retirer au registre des compétences</strong> si
              le départ est définitif, et à <strong>prononcer à quelqu’un d’autre</strong> si la paillasse perd
              sa couverture.
            </div>
          </Bloc>

          <PiedActions taille="sm" style={{ marginTop: 4 }} annuler={{ libelle: 'Fermer', onClick: onClose }} principale={{ libelle: 'Appliquer le transfert', libelleChargement: 'Transfert en cours…', chargement: busy, disabled: busy, onClick: () => void appliquer() }} />
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Chaque réaffectation est inscrite au journal du document concerné. Une catégorie laissée vide n’est pas touchée.
          </p>
        </div>
      )}
    </div>
  );
}
